import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine, faComments, faLightbulb, faTrophy, faUsers, faClock,
  faHeartPulse, faUserPlus, faPaperPlane,
  faCircleInfo, faCircleCheck, faFireFlameCurved, faBolt, faRobot,
  faGavel, faCoins, faSpinner, faEye, faWifi, faMobileScreen,
  faMapLocationDot, faCircle, faPlus, faPen, faBan, faPlay, faStop,
  faTrash, faImages, faTags, faXmark, faGift, faArrowRightToBracket, faFileLines,
  faVideo, faClipboardList, faHeart, faStar,
  faPhone, faTag, faBagShopping, faWandMagicSparkles, faCalculator,
} from "@fortawesome/free-solid-svg-icons";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  getAdminStats, getAdminRanking, getSignupsAnalytics, getBidsAnalytics,
  getAuctionsAnalytics, getPointsAnalytics, getRealtimeAnalytics, getOnlineUsers,
  getAdminAuctions, createAdminAuction, updateAdminAuction, deleteAdminAuction,
  activateAdminAuction, endAdminAuction, uploadAuctionImage, getAdminAuctionBids,
  getAdminAuctionWishes, markWishSeen, getAccessHistory,
  getAdminImages, toggleAdminImage, refreshImagesCache, getAdminUserDetails,
} from "../api/admin";
import PromoManagement from "./PromoManagement";
import SpinManagement from "./SpinManagement";
import SurveysColeta from "./SurveysColeta";
import TestimonialsManagement from "./TestimonialsManagement";
import FavoritesManagement from "./FavoritesManagement";
import CartRadarManagement from "./CartRadarManagement";
import ClientInterestsManagement from "./ClientInterestsManagement";
import instance from "../apiInstance";
import { formatAuctionImageUrl } from "../imageUrl";
import { GATE_STEP_LABELS, formatGateDate, gateInAppLabel } from "../gateLeadLabels";
import { prettyStoryTitle } from "../storyCart";
import "./AdminDashboard.css";
import "./UserModalActivity.css";

const TABS = [
  { key: "metrics", label: "Métricas Detalhadas", icon: faChartLine },
  { key: "users", label: "Base de Clientes", icon: faUsers },
  { key: "buyers", label: "Compradoras", icon: faBagShopping },
  { key: "access", label: "Histórico de Acessos", icon: faArrowRightToBracket },
  { key: "ranking", label: "Ranking Usuários", icon: faTrophy },
  { key: "insights", label: "Insights & Alertas", icon: faLightbulb },
  { key: "chat", label: "Chat Inteligente", icon: faComments },
  { key: "auctions", label: "Leilões", icon: faGavel },
  { key: "promos", label: "Promoções", icon: faTags },
  { key: "spins", label: "Roleta", icon: faGift },
  { key: "surveys", label: "Coleta de Dados", icon: faClipboardList },
  { key: "interests", label: "O que querem", icon: faWandMagicSparkles },
  { key: "favorites", label: "Favoritos", icon: faHeart },
  { key: "carts", label: "Carrinhos", icon: faBagShopping },
  { key: "testimonials", label: "Depoimentos", icon: faVideo },
  { key: "photos", label: "Fotos", icon: faImages },
];

const TIME_FILTERS = ["Hoje", "7 Dias", "30 Dias", "12 Meses"];
const PIE_COLORS = ["#d4a017", "#b8860b", "#8b6914", "#c0c0c0", "#cd7f32"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid rgba(212,175,55,0.15)", borderRadius: 10, padding: "8px 12px", fontSize: 12, color: "#1a1a1a", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
      <div style={{ color: "#999", marginBottom: 2 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ fontWeight: 700, color: i === 0 ? "#1a1a1a" : "#B8962E" }}>
          {p.name ? `${p.name}: ` : ""}{p.value}
        </div>
      ))}
    </div>
  );
};

function getRankClass(i) {
  if (i < 2) return `rank-${i + 1}`;
  if (i < 4) return `rank-${i + 1}`;
  if (i < 7) return `rank-${i + 1}`;
  return "rank-default";
}
function getTierClass(t) { return t.toLowerCase(); }

/* ── Map auto-fit ── */
// eslint-disable-next-line no-unused-vars
function FitBounds({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (markers.length === 0) return;
    const bounds = markers.map((m) => [m.lat, m.lng]);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
  }, [markers, map]);
  return null;
}

const BRAZIL_GEO_URL = "https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/brazil-states.geojson";

const STATE_NAME_TO_ABBR = {
  "Acre": "AC", "Alagoas": "AL", "Amapá": "AP", "Amapa": "AP", "Amazonas": "AM",
  "Bahia": "BA", "Ceará": "CE", "Ceara": "CE", "Distrito Federal": "DF", "Espírito Santo": "ES", "Espirito Santo": "ES",
  "Goiás": "GO", "Goias": "GO", "Maranhão": "MA", "Maranhao": "MA", "Mato Grosso": "MT", "Mato Grosso do Sul": "MS",
  "Minas Gerais": "MG", "Pará": "PA", "Para": "PA", "Paraíba": "PB", "Paraiba": "PB", "Paraná": "PR", "Parana": "PR",
  "Pernambuco": "PE", "Piauí": "PI", "Piaui": "PI", "Rio de Janeiro": "RJ", "Rio Grande do Norte": "RN",
  "Rio Grande do Sul": "RS", "Rondônia": "RO", "Rondonia": "RO", "Roraima": "RR", "Santa Catarina": "SC",
  "São Paulo": "SP", "Sao Paulo": "SP", "Sergipe": "SE", "Tocantins": "TO",
};

function getStateColor(count, maxCount) {
  if (!count) return "rgba(212,175,55,0.03)";
  const t = Math.min(count / Math.max(maxCount, 1), 1);
  const alpha = 0.12 + t * 0.68;
  return `rgba(184,150,46,${alpha.toFixed(2)})`;
}

const INITIAL_MESSAGES = [
  { from: "bot", text: "Olá! Sou o Agent de Dados. Posso analisar métricas em tempo real, ver quem está online e de onde estão acessando. O que quer saber?" },
];

function quizBrands(prefs) {
  const list = prefs?.favorite_brands || prefs?.selected_brands || [];
  return Array.isArray(list) ? list : [];
}

function hasQuizPrefs(prefs) {
  if (!prefs || typeof prefs !== "object") return false;
  return quizBrands(prefs).length > 0 || Boolean(prefs.produto_promo) || Boolean(prefs.selected_option);
}

function getBirthdayInfo(birthDateStr) {
  if (!birthDateStr) return null;
  const today = new Date();
  const birth = new Date(birthDateStr);
  const age = today.getFullYear() - birth.getUTCFullYear()
    - (today < new Date(today.getFullYear(), birth.getUTCMonth(), birth.getUTCDate()) ? 1 : 0);
  const nextBirthday = new Date(today.getFullYear(), birth.getUTCMonth(), birth.getUTCDate());
  if (nextBirthday < today) nextBirthday.setFullYear(today.getFullYear() + 1);
  const diffMs = nextBirthday - today;
  const daysUntil = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return { age, daysUntil, isToday: daysUntil === 0 };
}

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("metrics");
  const [timeFilter, setTimeFilter] = useState("30 Dias");
  const [chatMessages, setChatMessages] = useState(INITIAL_MESSAGES);
  const [chatInput, setChatInput] = useState("");

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [signupsChart, setSignupsChart] = useState([]);
  const [bidsChart, setBidsChart] = useState([]);
  const [auctionsData, setAuctionsData] = useState(null);
  const [, setPointsData] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [onlineData, setOnlineData] = useState(null);
  const [lastMetricsRefresh, setLastMetricsRefresh] = useState(null);
  const [accessHistory, setAccessHistory] = useState([]);
  const [accessHistoryGrouped, setAccessHistoryGrouped] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersGateTotal, setUsersGateTotal] = useState(0);
  const [usersSource, setUsersSource] = useState("app");
  const [usersPage, setUsersPage] = useState(0);
  const [usersSearch, setUsersSearch] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [buyersList, setBuyersList] = useState([]);
  const [buyersTotal, setBuyersTotal] = useState(0);
  const [buyersPage, setBuyersPage] = useState(0);
  const [buyersSearch, setBuyersSearch] = useState("");
  const [buyersLoading, setBuyersLoading] = useState(false);
  const [buyersError, setBuyersError] = useState(null);
  const [selectedUserModal, setSelectedUserModal] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [brazilGeo, setBrazilGeo] = useState(null);

  const [auctions, setAuctions] = useState([]);
  const [auctionFilter, setAuctionFilter] = useState("");
  const [auctionLoading, setAuctionLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAuction, setEditingAuction] = useState(null);
  const [formData, setFormData] = useState({
    title: "", description: "", images: [""], initial_price: "",
    start_time: "", end_time: "", category: "Bolsas", increment: "5",
    target_price: "", auto_bid_enabled: false,
    auto_bid_min_interval: "2", auto_bid_max_interval: "8",
    // Campos de pricing inteligente
    store_price: "", store_name: "", our_cost: "",
    max_discount_percent: "60", pricing_notes: "",
    // Novos campos do catálogo de produtos
    product_sku: "", reference_price_source: "",
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [expandedBids, setExpandedBids] = useState(null);
  const [auctionBids, setAuctionBids] = useState([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [wishes, setWishes] = useState([]);
  const [, setWishesLoading] = useState(false);
  // Estados do catálogo de produtos
  const [products, setProducts] = useState([]);
  const [searchProducts, setSearchProducts] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [broadcastConfig, setBroadcastConfig] = useState({
    enabled: false,
    auto_broadcast_on_activate: false,
    test_target: "pp 1522",
    wpp_group_name: "Grupo VIP Leilões",
    channels: {
      wpp_group: { enabled: true, name: "Grupo VIP Leilões" },
      sms: { enabled: true, sender_id: "CLUBE VIP" },
      wpp_official: { enabled: true, template_name: "auction_live_alert_v1" },
      pwa_push: { enabled: true },
    },
  });
  const [broadcastModal, setBroadcastModal] = useState(null);
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [broadcastTab, setBroadcastTab] = useState("wpp_group");
  const [selectedBroadcastChannels, setSelectedBroadcastChannels] = useState(["wpp_group", "sms", "wpp_official", "pwa_push"]);
  // Estados do Radar de Oportunidades & Match de Clientes
  const [radarData, setRadarData] = useState(null);
  const [, setRadarLoading] = useState(false);
  const [radarMatchModal, setRadarMatchModal] = useState(null);
  const auctionFileRef = React.useRef(null);

  const loadMetrics = useCallback(async (period, silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [statsRes, signupsRes, bidsRes, auctionsRes, pointsRes, realtimeRes] =
        await Promise.all([
          getAdminStats(period),
          getSignupsAnalytics(period),
          getBidsAnalytics(period),
          getAuctionsAnalytics(),
          getPointsAnalytics(),
          getRealtimeAnalytics(period).catch(() => null),
        ]);
      setStats(statsRes);
      setSignupsChart(signupsRes.data || []);
      setBidsChart(bidsRes.data || []);
      setAuctionsData(auctionsRes);
      setPointsData(pointsRes);
      setRealtime(realtimeRes);
      setLastMetricsRefresh(new Date());
    } catch (err) {
      console.error("Erro ao carregar métricas:", err);
      if (!silent) setError("Erro ao carregar dados. Verifique se o backend está rodando.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const loadRanking = useCallback(async (period) => {
    try {
      const res = await getAdminRanking(10, period);
      setRanking(res.ranking || []);
    } catch (err) { console.error(err); }
  }, []);

  const loadOnline = useCallback(async () => {
    try {
      const res = await getOnlineUsers();
      setOnlineData(res);
    } catch (err) { /* silencioso */ }
  }, []);

  const loadAccessHistory = useCallback(async (period) => {
    try {
      const res = await getAccessHistory(50, period);
      setAccessHistory(res.history || []);
    } catch (err) { console.error(err); }
  }, []);

  const loadUsers = useCallback(async (page = 0, search = usersSearch, source = usersSource) => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams({ skip: String(page * 20), limit: "20", source });
      if (search) params.set("search", search);
      const res = await instance.get(`/admin/users/list?${params}`);
      const data = res.data || {};
      setUsersList(data.users || []);
      setUsersTotal(data.total || 0);
      if (typeof data.total_gate_leads === "number") setUsersGateTotal(data.total_gate_leads);
      setUsersPage(page);
    } catch (e) { console.error(e); }
    finally { setUsersLoading(false); }
  }, [usersSearch, usersSource]);

  const loadBuyers = useCallback(async (page = 0, search = buyersSearch) => {
    setBuyersLoading(true);
    setBuyersError(null);
    try {
      const params = new URLSearchParams({ skip: String(page * 20), limit: "20" });
      if (search) params.set("search", search);
      const res = await instance.get(`/admin/buyers/list?${params}`);
      const data = res.data || {};
      setBuyersList(data.buyers || []);
      setBuyersTotal(data.total || 0);
      setBuyersPage(page);
      if (data.error === "crm_unconfigured") {
        setBuyersError("CRM não configurado na API (CRM_BASE_URL / CRM_INTEGRATION_KEY).");
      }
    } catch (e) {
      console.error(e);
      setBuyersError("Não foi possível carregar compradoras do CRM.");
    } finally {
      setBuyersLoading(false);
    }
  }, [buyersSearch]);

  const [userDetailsError, setUserDetailsError] = useState(false);

  const openUserModal = useCallback(async (user) => {
    setSelectedUserModal(user);
    setUserDetails(null);
    setUserDetailsLoading(true);
    setUserDetailsError(false);
    try {
      const res = await getAdminUserDetails(user.id);
      setUserDetails(res);
    } catch (e) {
      console.error(e);
      setUserDetailsError(true);
    } finally { setUserDetailsLoading(false); }
  }, []);

  const closeUserModal = useCallback(() => {
    setSelectedUserModal(null);
    setUserDetails(null);
  }, []);

  const getApiPeriod = (filterStr) => {
    if (filterStr === "Hoje") return "today";
    if (filterStr === "7 Dias") return "7d";
    if (filterStr === "30 Dias") return "30d";
    if (filterStr === "12 Meses") return "12m";
    return "30d";
  };

  useEffect(() => {
    const apiPeriod = getApiPeriod(timeFilter);
    loadMetrics(apiPeriod);
    loadRanking(apiPeriod);
    loadOnline();
    loadAccessHistory(apiPeriod);
  }, [loadMetrics, loadRanking, loadOnline, loadAccessHistory, timeFilter]);

  useEffect(() => {
    if (activeTab === "users") loadUsers(0);
  }, [activeTab, loadUsers]);

  useEffect(() => {
    if (activeTab === "buyers") loadBuyers(0);
  }, [activeTab, loadBuyers]);

  // Auto-refresh: online a cada 15s; KPIs e gráficos a cada 30s (silencioso)
  useEffect(() => {
    const apiPeriod = getApiPeriod(timeFilter);

    const refreshOnline = () => {
      loadOnline();
      getRealtimeAnalytics(apiPeriod).then(setRealtime).catch(() => {});
    };

    refreshOnline();
    const onlineId = setInterval(refreshOnline, 15_000);

    const refreshMetrics = () => {
      loadMetrics(apiPeriod, true);
      loadRanking(apiPeriod);
    };
    const metricsId = setInterval(refreshMetrics, 30_000);

    return () => {
      clearInterval(onlineId);
      clearInterval(metricsId);
    };
  }, [timeFilter, loadOnline, loadMetrics, loadRanking]);

  useEffect(() => {
    fetch(BRAZIL_GEO_URL).then((r) => r.json()).then(setBrazilGeo).catch(() => {});
  }, []);

  const CATEGORIES = ["Bolsas", "Sapatos", "Roupas", "Acessórios", "Joias", "Relógios", "Outros"];

  const loadAuctions = useCallback(async (statusF) => {
    setAuctionLoading(true);
    try {
      const res = await getAdminAuctions(statusF || undefined);
      setAuctions(res.auctions || []);
    } catch (e) { console.error(e); }
    finally { setAuctionLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === "auctions") {
      loadAuctions(auctionFilter);
      loadWishes();
      loadBroadcastConfig();
      loadRadarOpportunities();
    }
  }, [activeTab, auctionFilter, loadAuctions]);

  const loadRadarOpportunities = async () => {
    setRadarLoading(true);
    try {
      const res = await instance.get("/admin/auctions/radar-opportunities");
      if (res.data) setRadarData(res.data);
    } catch (e) {
      console.error("Erro ao carregar radar:", e);
    } finally {
      setRadarLoading(false);
    }
  };

  const handleOpenRadarMatch = async (auction) => {
    try {
      const res = await instance.get(`/admin/auctions/${auction.id}/radar-match`);
      setRadarMatchModal({
        auction,
        matchData: res.data,
      });
    } catch (err) {
      alert("Erro ao buscar match no radar: " + (err?.response?.data?.detail || err.message));
    }
  };

  const handleLaunchAuctionFromRadar = (opp) => {
    if (opp.composer_match) {
      handleSelectProduct(opp.composer_match);
    } else {
      resetForm();
      setSearchProducts(opp.term);
      searchProductsBySku(opp.term);
      setFormData((prev) => ({
        ...prev,
        title: opp.term,
        pricing_notes: `Origem: Radar de Oportunidades (${opp.reason})`,
      }));
    }
    setShowForm(true);
  };

  const loadBroadcastConfig = async () => {
    try {
      const res = await instance.get("/admin/auctions/broadcast/config");
      if (res.data) setBroadcastConfig((prev) => ({ ...prev, ...res.data }));
    } catch {}
  };

  const handleSaveBroadcastConfig = async (updated) => {
    try {
      const res = await instance.post("/admin/auctions/broadcast/config", updated);
      if (res.data?.config) {
        setBroadcastConfig(res.data.config);
        alert("Configuração de disparos atualizada com sucesso!");
      }
    } catch (err) {
      alert("Erro ao salvar configuração: " + (err?.response?.data?.detail || err.message));
    }
  };

  const handlePreviewBroadcast = async (auction) => {
    setBroadcastLoading(true);
    try {
      const res = await instance.get(`/admin/auctions/${auction.id}/broadcast/preview?event_type=live`);
      setBroadcastModal({
        auction,
        preview: res.data?.preview,
        payloads: res.data?.payloads,
        audience: res.data?.audience,
        config: res.data?.config,
        dispatchResult: null,
      });
    } catch (err) {
      alert("Erro ao gerar preview: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBroadcastLoading(false);
    }
  };

  const handleDispatchBroadcast = async (mode = "simulate") => {
    if (!broadcastModal?.auction) return;
    if (mode === "live" && !broadcastConfig.enabled) {
      if (!window.confirm("⚠️ ATENÇÃO: O switch global de envio real está desativado. Deseja executar no modo Simulação Segura (Dry-Run)?")) {
        return;
      }
      mode = "simulate";
    }

    setBroadcastLoading(true);
    try {
      const res = await instance.post(`/admin/auctions/${broadcastModal.auction.id}/broadcast/dispatch`, {
        mode,
        channels: selectedBroadcastChannels,
        target: broadcastConfig.test_target || "pp 1522",
        event_type: "live",
      });
      setBroadcastModal((prev) => prev ? { ...prev, dispatchResult: res.data?.result } : null);
      if (mode === "simulate") {
        alert("🧪 Simulação multi-canal concluída com sucesso! Veja o log detalhado abaixo.");
      } else if (mode === "test") {
        alert(`📱 Disparo de teste executado com sucesso para "${broadcastConfig.test_target || "pp 1522"}"!`);
      } else {
        alert("🚀 Disparo oficial enviado para todos os canais ativos!");
      }
    } catch (err) {
      alert("Erro ao processar disparo: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBroadcastLoading(false);
    }
  };

  const loadWishes = async () => {
    setWishesLoading(true);
    try {
      const data = await getAdminAuctionWishes();
      setWishes(data || []);
    } catch {} finally { setWishesLoading(false); }
  };

  const handleMarkWishSeen = async (wishId) => {
    try {
      await markWishSeen(wishId);
      setWishes((prev) => prev.map((w) => w.id === wishId ? { ...w, seen: true } : w));
    } catch {}
  };

  // Funções do catálogo de produtos e Composer (CentraGF)
  const [composerLoading, setComposerLoading] = useState(false);

  const searchProductsBySku = async (query) => {
    if (!query || query.length < 2) {
      setProducts([]);
      return;
    }
    setComposerLoading(true);
    try {
      const res = await instance.get(`/admin/composer/search?q=${encodeURIComponent(query)}&limit=15`);
      setProducts(res.data.products || []);
    } catch (err) {
      console.error("Erro ao buscar no Composer:", err);
      setProducts([]);
    } finally {
      setComposerLoading(false);
    }
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    
    const rec = product.pricing_recommendation || {};
    const storePrice = product.store_price || product.best_reference_price || "";
    const ourCost = product.our_cost || product.promo_price || "";
    const initialPrice = rec.suggested_initial_price ? String(rec.suggested_initial_price) : "";
    const targetPrice = rec.suggested_target_price ? String(rec.suggested_target_price) : "";
    const storeName = rec.store_name || (product.brand ? `${product.brand} Oficial` : "Loja Oficial");
    
    // Auto-preencher campos do leilão
    setFormData((prev) => ({
      ...prev,
      product_sku: product.sku || product.local_id || "",
      title: product.title || (product.brand ? `${product.brand} ${product.model || ""}`.trim() : prev.title),
      description: `Autêntica ${product.brand || ""} ${product.title || ""}. Peça de catálogo em perfeito estado, acompanhada de todos os acessórios originais.`,
      category: product.category || "Bolsas",
      images: product.images?.length ? [...product.images] : (product.image_url ? [product.image_url] : prev.images),
      store_price: storePrice ? String(storePrice) : prev.store_price,
      store_name: storeName,
      our_cost: ourCost ? String(ourCost) : prev.our_cost,
      max_discount_percent: "60",
      initial_price: initialPrice || prev.initial_price,
      target_price: targetPrice || prev.target_price,
      auto_bid_enabled: true,
      pricing_notes: `Origem: Composer CentraGF (${product.source_lote || "catálogo"}). ${product.anchor ? `Âncora oficial: US$ ${product.anchor.official_usd || "?"}` : ""}`,
    }));
  };

  const resetForm = () => {
    setFormData({
      title: "", description: "", images: [""], initial_price: "", start_time: "", end_time: "",
      category: "Bolsas", increment: "5", target_price: "", auto_bid_enabled: false,
      auto_bid_min_interval: "2", auto_bid_max_interval: "8",
      store_price: "", store_name: "", our_cost: "", max_discount_percent: "60", pricing_notes: "",
      product_sku: "", reference_price_source: ""
    });
    setEditingAuction(null);
    setShowForm(false);
  };

  const openCreateForm = () => {
    resetForm();
    setShowForm(true);
  };

  const toLocalBrasilia = (utcStr) => {
    if (!utcStr) return "";
    try {
      const d = new Date(utcStr);
      const brt = new Date(d.getTime() - 3 * 3600000);
      const y = brt.getUTCFullYear();
      const mo = String(brt.getUTCMonth() + 1).padStart(2, "0");
      const day = String(brt.getUTCDate()).padStart(2, "0");
      const h = String(brt.getUTCHours()).padStart(2, "0");
      const mi = String(brt.getUTCMinutes()).padStart(2, "0");
      return `${y}-${mo}-${day}T${h}:${mi}`;
    } catch { return ""; }
  };

  const openEditForm = (a) => {
    setEditingAuction(a);
    setFormData({
      title: a.title || "",
      description: a.description || "",
      images: a.images?.length ? [...a.images] : [""],
      initial_price: String(a.initial_price || ""),
      start_time: toLocalBrasilia(a.start_time),
      end_time: toLocalBrasilia(a.end_time),
      category: a.category || "Bolsas",
      increment: String(a.increment || 5),
      target_price: a.target_price ? String(a.target_price) : "",
      status: a.status || "pending",
      auto_bid_enabled: a.auto_bid_enabled || false,
      auto_bid_min_interval: String(a.auto_bid_min_interval || 2),
      auto_bid_max_interval: String(a.auto_bid_max_interval || 8),
      // Campos de pricing
      store_price: a.store_price ? String(a.store_price) : "",
      store_name: a.store_name || "",
      our_cost: a.our_cost ? String(a.our_cost) : "",
      max_discount_percent: String(a.max_discount_percent || 60),
      pricing_notes: a.pricing_notes || "",
      // Novos campos do catálogo
      product_sku: a.product_sku || "",
      reference_price_source: a.reference_price_source || "",
    });
    setShowForm(true);
  };

  const handleFormChange = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    
    // Auto-calcular preço inicial sugerido quando store_price ou max_discount_percent mudarem
    if (field === "store_price" || field === "max_discount_percent") {
      setTimeout(() => {
        setFormData((current) => {
          const storePrice = parseFloat(field === "store_price" ? value : current.store_price);
          const discount = parseInt(field === "max_discount_percent" ? value : current.max_discount_percent);
          
          if (storePrice && discount && (!current.initial_price || current.initial_price === "")) {
            const suggestedPrice = (storePrice * (100 - discount) / 100 * 1.1).toFixed(2); // 10% acima do mínimo
            return { ...current, initial_price: suggestedPrice };
          }
          return current;
        });
      }, 100);
    }
  };

  const toBrasiliaUTC = (localDateStr) => {
    if (!localDateStr) return "";
    const [date, time] = localDateStr.split("T");
    if (!date || !time) return new Date(localDateStr).toISOString();
    return `${date}T${time}:00-03:00`;
  };

  const handleImageChange = (idx, value) => {
    const imgs = [...formData.images];
    imgs[idx] = value;
    setFormData((p) => ({ ...p, images: imgs }));
  };
  const addImageField = () => { if (formData.images.length < 10) setFormData((p) => ({ ...p, images: [...p.images, ""] })); };
  const removeImageField = (idx) => {
    if (formData.images.length <= 1) return;
    setFormData((p) => ({ ...p, images: p.images.filter((_, i) => i !== idx) }));
  };

  const handleAuctionFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingImages(true);
    try {
      const urls = [];
      for (const file of files) {
        const res = await uploadAuctionImage(file);
        if (res.url) urls.push(res.url);
      }
      setFormData((p) => {
        const existing = p.images.filter((u) => u.trim());
        const merged = [...existing, ...urls].slice(0, 10);
        return { ...p, images: merged.length ? merged : [""] };
      });
    } catch (err) {
      alert(err?.response?.data?.detail || "Erro ao fazer upload");
    } finally {
      setUploadingImages(false);
      if (auctionFileRef.current) auctionFileRef.current.value = "";
    }
  };

  const handleSaveAuction = async () => {
    const images = formData.images.filter((u) => u.trim());
    if (!formData.title || !formData.description || images.length === 0 || !formData.initial_price || !formData.start_time || !formData.end_time) {
      alert("Preencha todos os campos obrigatórios.");
      return;
    }
    const payload = {
      title: formData.title,
      description: formData.description,
      images,
      initial_price: parseFloat(formData.initial_price),
      start_time: toBrasiliaUTC(formData.start_time),
      end_time: toBrasiliaUTC(formData.end_time),
      category: formData.category,
      increment: parseFloat(formData.increment) || 5,
      target_price: formData.target_price ? parseFloat(formData.target_price) : null,
      auto_bid_enabled: formData.auto_bid_enabled,
      auto_bid_min_interval: parseInt(formData.auto_bid_min_interval) || 2,
      auto_bid_max_interval: parseInt(formData.auto_bid_max_interval) || 8,
      // Campos de pricing inteligente
      store_price: formData.store_price ? parseFloat(formData.store_price) : null,
      store_name: formData.store_name || null,
      our_cost: formData.our_cost ? parseFloat(formData.our_cost) : null,
      max_discount_percent: parseInt(formData.max_discount_percent) || 60,
      pricing_notes: formData.pricing_notes || null,
    };
    try {
      if (editingAuction) {
        await updateAdminAuction(editingAuction.id, payload);
      } else {
        await createAdminAuction(payload);
      }
      resetForm();
      loadAuctions(auctionFilter);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro ao salvar leilão");
    }
  };

  const toggleBidsPanel = async (auctionId) => {
    if (expandedBids === auctionId) {
      setExpandedBids(null);
      setAuctionBids([]);
      return;
    }
    setExpandedBids(auctionId);
    setBidsLoading(true);
    try {
      const bids = await getAdminAuctionBids(auctionId);
      setAuctionBids(bids || []);
    } catch { setAuctionBids([]); }
    finally { setBidsLoading(false); }
  };

  useEffect(() => {
    if (!expandedBids) return;
    const iv = setInterval(async () => {
      try {
        const bids = await getAdminAuctionBids(expandedBids);
        setAuctionBids(bids || []);
      } catch {}
    }, 5000);
    return () => clearInterval(iv);
  }, [expandedBids]);

  const handleAuctionAction = async (id, action) => {
    const confirmed = window.confirm(
      action === "cancel" ? "Tem certeza que deseja cancelar este leilão?" :
      action === "end" ? "Tem certeza que deseja encerrar este leilão?" :
      "Ativar este leilão agora?"
    );
    if (!confirmed) return;
    try {
      if (action === "cancel") await deleteAdminAuction(id);
      else if (action === "activate") await activateAdminAuction(id);
      else if (action === "end") await endAdminAuction(id);
      loadAuctions(auctionFilter);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro na ação");
    }
  };

  const handleSendMessage = () => {
    const text = chatInput.trim();
    if (!text) return;
    setChatMessages((prev) => [...prev, { from: "user", text }]);
    setChatInput("");
    setTimeout(() => {
      const s = stats || {};
      const r = realtime || {};
      const o = onlineData || {};
      const onlineList = (o.users || []).map((u) => u.name).slice(0, 5).join(", ");
      const locations = (o.location_clusters || []).slice(0, 3).map((c) => `${c.city}(${c.count})`).join(", ");
      const responses = [
        `Agora: ${o.online_count || 0} online. ${onlineList ? `Usuárias: ${onlineList}.` : ""} ${locations ? `Localizações: ${locations}.` : ""} ${s.active_users || 0} ativas no período, tempo médio ${s.avg_session_minutes || 0}min.`,
        `Mapa: ${(o.location_clusters || []).length} cidades com usuárias cadastradas. ${o.online_count || 0} conectadas agora. ${r.page_views || 0} page views. Top páginas: ${(r.pages_chart || []).slice(0, 3).map((p) => p.name).join(", ") || "sem dados"}.`,
        `Resumo: ${s.total_users || 0} total, ${s.new_users || 0} novas (${s.new_users_change || 0}%), ${s.premium_count || 0} premium, ${s.total_bids || 0} lances, ${s.active_auctions || 0} leilões ativos.`,
      ];
      setChatMessages((prev) => [...prev, { from: "bot", text: responses[Math.floor(Math.random() * responses.length)] }]);
    }, 1200);
  };

  const generateInsights = () => {
    if (!stats) return [];
    const insights = [];
    const r = realtime || {};
    const o = onlineData || {};

    if ((o.online_count || 0) > 0) {
      const names = (o.users || []).slice(0, 5).map((u) => u.name).join(", ");
      insights.push({ type: "success", icon: faWifi, title: `${o.online_count} online agora`, desc: `Usuárias conectadas: ${names || "carregando..."}. ${r.active_users || 0} ativas no período.`, time: "Atualiza a cada 15s" });
    }

    if ((o.location_clusters || []).length > 0) {
      const top = o.location_clusters.sort((a, b) => b.count - a.count).slice(0, 5);
      const locStr = top.map((c) => `${c.city}/${c.state} (${c.count})`).join(", ");
      insights.push({ type: "info", icon: faMapLocationDot, title: "Distribuição geográfica", desc: `Principais cidades: ${locStr}.`, time: "Baseado no DDD + IP" });
    }

    if (stats.new_users_change > 10) {
      insights.push({ type: "success", icon: faCircleCheck, title: "Crescimento acelerado", desc: `Cadastros +${stats.new_users_change}% vs anterior. ${stats.new_users} novas usuárias.`, time: "Tempo real" });
    } else if (stats.new_users_change < -10) {
      insights.push({ type: "danger", icon: faFireFlameCurved, title: "Queda nos cadastros", desc: `Cadastros ${stats.new_users_change}%. Considere campanhas.`, time: "Tempo real" });
    }

    if (r.pages_chart?.length > 0) {
      insights.push({ type: "info", icon: faEye, title: "Páginas mais acessadas", desc: `Top: ${r.pages_chart.slice(0, 3).map((p) => `${p.name} (${p.value}x)`).join(", ")}. ${r.page_views || 0} views total.`, time: "Tempo real" });
    }
    if (r.devices_chart?.length > 0) {
      insights.push({ type: "info", icon: faMobileScreen, title: "Dispositivos", desc: r.devices_chart.map((d) => `${d.name}: ${d.value}`).join(", "), time: "Tempo real" });
    }
    if (stats.premium_count > 0) {
      const rate = ((stats.premium_count / stats.total_users) * 100).toFixed(1);
      insights.push({ type: "success", icon: faBolt, title: "Conversão Premium", desc: `${stats.premium_count} premium (${rate}%). ${stats.total_referrals} indicações.`, time: "Tempo real" });
    }
    if (stats.total_points > 0) {
      insights.push({ type: "info", icon: faCoins, title: "Gamificação", desc: `${stats.total_points.toLocaleString("pt-BR")} pontos distribuídos.`, time: "Tempo real" });
    }
    if (stats.total_users > 0 && insights.length === 0) {
      insights.push({ type: "info", icon: faCircleInfo, title: "Aguardando dados", desc: `${stats.total_users} usuárias na base. Dados de tracking aparecerão quando usarem o app.`, time: "Sistema operacional" });
    }
    return insights;
  };

  /* ── Online banner + lista ── */
  const renderOnlineBanner = () => {
    const count = onlineData?.online_count || stats?.online_now || 0;
    const users = onlineData?.users || [];
    return (
      <div className="admin-online-section">
      <div className="admin-online-banner">
        <div className="admin-online-left">
          <span className="admin-online-dot" />
          <span className="admin-online-count">{count}</span>
          <span className="admin-online-label">
              {count === 1 ? "usuária online agora" : "usuárias online agora"}
          </span>
        </div>
          <span className="admin-online-refresh">
            Online 15s · métricas 30s
            {lastMetricsRefresh && (
              <> · atualizado {lastMetricsRefresh.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</>
            )}
              </span>
        </div>

        {users.length > 0 && (
          <div className="admin-online-list">
            {users.map((u, i) => (
              <div key={u.user_id || i} className="admin-online-row">
                <div className="admin-online-row-dot" />
                <div className="admin-online-row-avatar">
                  {(u.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="admin-online-row-info">
                  <div className="admin-online-row-name">{u.name || "Sem nome"}</div>
                  <div className="admin-online-row-detail">
                    {u.email}
                  </div>
                </div>
                <div className="admin-online-row-meta">
                  {u.current_page && (
                    <span className="admin-online-row-page">{u.current_page}</span>
                  )}
                  {u.device && (
                    <span className="admin-online-row-device">{u.device}</span>
                  )}
                </div>
                {u.location && (
                  <div className="admin-online-row-location">
                    {u.location.city}{u.location.state ? `/${u.location.state}` : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {count === 0 && (
          <div className="admin-online-empty">
            Nenhuma usuária online no momento.
          </div>
        )}
      </div>
    );
  };

  /* ── Map (choropleth por estado) ── */
  const renderMap = () => {
    const clusters = onlineData?.location_clusters || [];
    const onlineMarkers = onlineData?.map_markers || [];

    if (!brazilGeo && clusters.length === 0) {
      return (
        <div className="admin-chart-card" style={{ textAlign: "center", padding: "40px 20px", color: "#bbb" }}>
          <FontAwesomeIcon icon={faMapLocationDot} style={{ fontSize: 32, marginBottom: 10 }} />
          <div>Mapa aparecerá quando houver dados de localização (DDD ou IP).</div>
        </div>
      );
    }

    const stateCounts = {};
    clusters.forEach((c) => {
      if (c.state) {
        // Normalizar caso venha nome completo do IP-API ou sigla do DDD
        let abbr = c.state;
        if (c.state.length > 2) {
          abbr = STATE_NAME_TO_ABBR[c.state] || c.state;
        }
        stateCounts[abbr] = (stateCounts[abbr] || 0) + c.count;
      }
    });
    const totalMapped = Object.values(stateCounts).reduce((a, b) => a + b, 0);
    const maxCount = Math.max(...Object.values(stateCounts), 1);
    const stateList = Object.entries(stateCounts).sort((a, b) => b[1] - a[1]);

    const geoStyle = (feature) => {
      const abbr = STATE_NAME_TO_ABBR[feature.properties.name] || "";
      const count = stateCounts[abbr] || 0;
      return {
        fillColor: getStateColor(count, maxCount),
        fillOpacity: 1,
        color: count > 0 ? "#B8962E" : "rgba(0,0,0,0.12)",
        weight: count > 0 ? 1.5 : 0.5,
      };
    };

    const onEachState = (feature, layer) => {
      const name = feature.properties.name;
      const abbr = STATE_NAME_TO_ABBR[name] || "";
      const count = stateCounts[abbr] || 0;
      layer.bindTooltip(
        `<div class="map-tt"><strong>${name} (${abbr})</strong><br/><span class="map-tt-count">${count}</span> usuária${count !== 1 ? "s" : ""}</div>`,
        { sticky: true, direction: "top", className: "admin-map-tooltip" }
      );
      layer.on({
        mouseover: (e) => {
          e.target.setStyle({ weight: 2.5, color: "#D4AF37", fillOpacity: 1 });
          e.target.bringToFront();
        },
        mouseout: (e) => {
          e.target.setStyle(geoStyle(feature));
        },
      });
    };

    return (
      <div className="admin-chart-card admin-map-card">
        <h3 className="admin-chart-title">
          <FontAwesomeIcon icon={faMapLocationDot} style={{ marginRight: 8 }} />
          Distribuição por Estado
        </h3>
        <p className="admin-chart-subtitle">
          {totalMapped} usuárias mapeadas em {stateList.length} estados
          {onlineMarkers.length > 0 && ` • ${onlineMarkers.length} online agora`}
        </p>
        <div style={{ height: 420, borderRadius: 12, overflow: "hidden" }}>
          <MapContainer
            center={[-14.24, -51.93]}
            zoom={4}
            style={{ height: "100%", width: "100%" }}
            zoomControl={false}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution=""
              url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
            />

            {brazilGeo && (
              <GeoJSON
                key={JSON.stringify(stateCounts)}
                data={brazilGeo}
                style={geoStyle}
                onEachFeature={onEachState}
              />
            )}

            {onlineMarkers.map((m, i) => (
              <CircleMarker
                key={`on-${i}`}
                center={[m.lat, m.lng]}
                radius={7}
                pathOptions={{ color: "#fff", fillColor: "#34c759", fillOpacity: 0.9, weight: 2 }}
              >
                <Popup>
                  <div style={{ color: "#1a1a1a", fontWeight: 600, fontSize: 13 }}>
                    {m.name}<br />
                    <span style={{ fontWeight: 400 }}>
                      {m.city && `${m.city} • `}{m.device && `${m.device} • `}{m.page}
                    </span>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>

        <div className="admin-map-legend">
          <div className="admin-map-legend-scale">
            <span className="admin-map-legend-label">Menos</span>
            <div className="admin-map-legend-bar" />
            <span className="admin-map-legend-label">Mais</span>
        </div>
          {onlineMarkers.length > 0 && (
            <span className="admin-map-legend-online">
              <FontAwesomeIcon icon={faCircle} style={{ color: "#34c759", fontSize: 8 }} /> Online agora
            </span>
          )}
        </div>

        {stateList.length > 0 && (
          <div className="admin-map-ranking">
            {stateList.slice(0, 8).map(([abbr, count]) => (
              <div key={abbr} className="admin-map-rank-item">
                <span className="admin-map-rank-abbr">{abbr}</span>
                <div className="admin-map-rank-bar-bg">
                  <div className="admin-map-rank-bar-fill" style={{ width: `${(count / maxCount) * 100}%` }} />
                </div>
                <span className="admin-map-rank-count">{count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderLoading = () => (
    <div style={{ textAlign: "center", padding: "60px 0", color: "#aaa" }}>
      <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 28, marginBottom: 12 }} />
      <div>Carregando dados...</div>
    </div>
  );

  const renderError = () => (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div style={{ color: "#ff453a", fontSize: 14, marginBottom: 8 }}>{error}</div>
      <button onClick={() => loadMetrics(getApiPeriod(timeFilter))} style={{ background: "rgba(212,160,23,0.15)", color: "#d4a017", border: "1px solid rgba(212,160,23,0.3)", borderRadius: 10, padding: "8px 20px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
        Tentar novamente
      </button>
    </div>
  );

  const renderMetrics = () => {
    if (loading && !stats) return renderLoading();
    if (error && !stats) return renderError();
    if (!stats) return null;
    const r = realtime || {};

    return (
      <>
        <div className="admin-time-filter">
          {TIME_FILTERS.map((f) => (
            <button key={f} className={`admin-time-btn ${timeFilter === f ? "active" : ""}`} onClick={() => setTimeFilter(f)}>{f}</button>
          ))}
        </div>

        {renderOnlineBanner()}

        <div className="admin-kpi-grid admin-kpi-highlight">
          <div className="admin-kpi-card kpi-accent">
            <div className="admin-kpi-top"><span className="admin-kpi-label">Total de Acessos</span><span className="admin-kpi-icon"><FontAwesomeIcon icon={faArrowRightToBracket} /></span></div>
            <div className="admin-kpi-value">{stats.total_sessions_alltime || 0}</div>
            <div className="admin-kpi-change positive">{stats.total_sessions_period || 0} no período</div>
          </div>
          <div className="admin-kpi-card kpi-accent">
            <div className="admin-kpi-top"><span className="admin-kpi-label">Total Page Views</span><span className="admin-kpi-icon"><FontAwesomeIcon icon={faFileLines} /></span></div>
            <div className="admin-kpi-value">{stats.total_pageviews_alltime || 0}</div>
            <div className="admin-kpi-change positive">{stats.total_pageviews_period || 0} no período</div>
          </div>
          <div className="admin-kpi-card">
            <div className="admin-kpi-top"><span className="admin-kpi-label">Usuárias Ativas</span><span className="admin-kpi-icon"><FontAwesomeIcon icon={faHeartPulse} /></span></div>
            <div className="admin-kpi-value">{stats.active_users || 0}</div>
            <div className="admin-kpi-change positive">{stats.online_now || 0} online agora</div>
          </div>
          <div className="admin-kpi-card">
            <div className="admin-kpi-top"><span className="admin-kpi-label">Tempo Médio</span><span className="admin-kpi-icon"><FontAwesomeIcon icon={faClock} /></span></div>
            <div className="admin-kpi-value">{stats.avg_session_minutes || 0}m</div>
            <div className="admin-kpi-change neutral">Por sessão</div>
          </div>
        </div>

        <div className="admin-kpi-grid" style={{ marginBottom: 20 }}>
          <div className="admin-kpi-card">
            <div className="admin-kpi-top"><span className="admin-kpi-label">Total Usuárias</span><span className="admin-kpi-icon"><FontAwesomeIcon icon={faUsers} /></span></div>
            <div className="admin-kpi-value">{stats.total_users}</div>
            <div className="admin-kpi-change neutral">Base cadastrada</div>
          </div>
          <div className="admin-kpi-card">
            <div className="admin-kpi-top"><span className="admin-kpi-label">Novos Cadastros</span><span className="admin-kpi-icon"><FontAwesomeIcon icon={faUserPlus} /></span></div>
            <div className="admin-kpi-value">{stats.new_users}</div>
            <div className={`admin-kpi-change ${stats.new_users_change >= 0 ? "positive" : "negative"}`}>{stats.new_users_change >= 0 ? "+" : ""}{stats.new_users_change}% vs anterior</div>
          </div>
          <div className="admin-kpi-card">
            <div className="admin-kpi-top"><span className="admin-kpi-label">Premium</span><span className="admin-kpi-icon" style={{ color: "#34c759", background: "rgba(52,199,89,0.1)" }}><FontAwesomeIcon icon={faHeartPulse} /></span></div>
            <div className="admin-kpi-value" style={{ color: "#34c759" }}>{stats.premium_count}</div>
            <div className="admin-kpi-change neutral">{stats.total_referrals} indicações</div>
          </div>
          <div className="admin-kpi-card">
            <div className="admin-kpi-top"><span className="admin-kpi-label">Lances</span><span className="admin-kpi-icon"><FontAwesomeIcon icon={faCoins} /></span></div>
            <div className="admin-kpi-value">{stats.total_bids}</div>
            <div className="admin-kpi-change neutral">{stats.recent_bids} no período</div>
          </div>
        </div>

        {/* Map */}
        {renderMap()}

        {stats.access_chart?.length > 0 && (
          <div className="admin-chart-card" style={{ marginTop: 14, marginBottom: 14 }}>
            <h3 className="admin-chart-title">
              <FontAwesomeIcon icon={faArrowRightToBracket} style={{ marginRight: 8 }} />
              Histórico de Acessos
            </h3>
            <p className="admin-chart-subtitle">
              Sessões e page views por dia — {stats.total_sessions_alltime || 0} acessos totais
            </p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stats.access_chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fill: "#999", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#999", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="sessoes" name="Sessões" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                <Bar dataKey="pageviews" name="Page Views" fill="#B8962E" radius={[4, 4, 0, 0]} opacity={0.5} />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: "flex", gap: 20, marginTop: 8, fontSize: 11, color: "#aaa" }}>
              <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: "#D4AF37", marginRight: 6 }} />Sessões (acessos)</span>
              <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: "#B8962E", opacity: 0.5, marginRight: 6 }} />Page Views</span>
            </div>
          </div>
        )}

        <div className="admin-chart-grid" style={{ marginTop: 14 }}>
          <div className="admin-chart-card">
            <h3 className="admin-chart-title">Novos Cadastros</h3>
            <p className="admin-chart-subtitle">Evolução no período ({stats.new_users} total)</p>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={signupsChart}>
                <defs><linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#d4a017" stopOpacity={0.4} /><stop offset="100%" stopColor="#d4a017" stopOpacity={0.02} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fill: "#999", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#999", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="value" stroke="#d4a017" strokeWidth={2} fill="url(#goldGrad)" dot={{ r: 3, fill: "#d4a017" }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="admin-chart-card">
            <h3 className="admin-chart-title">Lances</h3>
            <p className="admin-chart-subtitle">{stats.total_bids} totais ({stats.recent_bids} no período)</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={bidsChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fill: "#999", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#999", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" fill="#d4a017" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {r.screen_time_chart?.length > 0 && (
            <div className="admin-chart-card">
              <h3 className="admin-chart-title">Tempo de Tela</h3>
              <p className="admin-chart-subtitle">Atividade por hora do dia</p>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={r.screen_time_chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="name" tick={{ fill: "#999", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#999", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="value" stroke="#d4a017" strokeWidth={2} dot={{ r: 4, fill: "#d4a017", stroke: "#0a0a0a", strokeWidth: 2 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {r.devices_chart?.length > 0 && (
            <div className="admin-chart-card">
              <h3 className="admin-chart-title">Dispositivos</h3>
              <p className="admin-chart-subtitle">Distribuição por plataforma</p>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={r.devices_chart} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" stroke="none" label={({ name, value }) => `${name} ${value}`}>
                    {r.devices_chart.map((e, i) => <Cell key={e.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          {r.pages_chart?.length > 0 && (
            <div className="admin-chart-card">
              <h3 className="admin-chart-title">Páginas Mais Acessadas</h3>
              <p className="admin-chart-subtitle">{r.page_views} views total</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={r.pages_chart} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis type="number" tick={{ fill: "#999", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" tick={{ fill: "#999", fontSize: 11 }} axisLine={false} tickLine={false} width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#b8860b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {auctionsData?.by_status?.length > 0 && (
            <div className="admin-chart-card">
              <h3 className="admin-chart-title">Leilões por Status</h3>
              <p className="admin-chart-subtitle">{auctionsData.total_auctions} leilões • R$ {auctionsData.total_revenue?.toLocaleString("pt-BR")}</p>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={auctionsData.by_status} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" stroke="none" label={({ name, value }) => `${name} (${value})`}>
                    {auctionsData.by_status.map((e, i) => <Cell key={e.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderRanking = () => {
    if (ranking.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa" }}>
          {loading ? <><FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 24, marginBottom: 8 }} /><div>Carregando...</div></> : <div>Nenhum dado disponível.</div>}
        </div>
      );
    }
    return (
      <>
        {renderOnlineBanner()}
        <div className="admin-ranking-header"><h2>Ranking de Usuários Mais Engajados</h2><p>Por pontos, sessões e tempo de uso</p></div>
        <div className="admin-ranking-list">
          {ranking.map((user, i) => (
            <div key={user.email || i} className={`admin-ranking-item tier-${getTierClass(user.tier)}`}>
              <div className={`admin-rank-number ${getRankClass(i)}`}>{user.position || i + 1}</div>
              <div className="admin-rank-info">
                <div className="admin-rank-name">{user.name}{user.is_premium && <span className="trophy">👑</span>}{i < 7 && !user.is_premium && <span className="trophy">🏆</span>}</div>
                <div className="admin-rank-stats" style={{ color: "#888", marginBottom: 2 }}>{user.email}</div>
                <div className="admin-rank-stats">
                  {user.points} pts • {user.sessions || 0} sessões • {user.total_time || "0m"} • {user.bids} lances
                </div>
                {user.created_at && (
                  <div className="admin-rank-stats" style={{ fontSize: 10, marginTop: 4 }}>
                    Cadastrada em: {new Date(user.created_at).toLocaleDateString("pt-BR")}
                  </div>
                )}
              </div>
              <span className={`admin-rank-badge badge-${getTierClass(user.tier)}`}>{user.tier}</span>
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderAccessHistory = () => {
    if (accessHistory.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa" }}>
          {loading ? <><FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 24, marginBottom: 8 }} /><div>Carregando...</div></> : <div>Nenhum histórico de acesso encontrado.</div>}
        </div>
      );
    }

    let displayList = accessHistory;
    if (accessHistoryGrouped) {
      const grouped = {};
      accessHistory.forEach(sess => {
        if (!grouped[sess.user_id]) {
          grouped[sess.user_id] = { ...sess };
        }
      });
      displayList = Object.values(grouped);
    }

    return (
      <>
        {renderOnlineBanner()}
        <div className="admin-ranking-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
          <div>
            <h2>Histórico de Acessos Recentes</h2>
            <p>Últimas 50 sessões de clientes no app</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button 
              onClick={() => setAccessHistoryGrouped(false)}
              style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #d4a017", background: !accessHistoryGrouped ? "#d4a017" : "transparent", color: !accessHistoryGrouped ? "#fff" : "#d4a017", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
            >
              Ordem Cronológica
            </button>
            <button 
              onClick={() => setAccessHistoryGrouped(true)}
              style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #d4a017", background: accessHistoryGrouped ? "#d4a017" : "transparent", color: accessHistoryGrouped ? "#fff" : "#d4a017", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
            >
              Agrupar por Cliente
            </button>
          </div>
        </div>
        <div className="admin-ranking-list">
          {displayList.map((sess, i) => {
            const isOnline = Boolean(sess.is_online);
            let durationStr = "—";
            const ds = sess.duration_seconds;
            if (isOnline) {
              const start = new Date(sess.start_time);
              const now = new Date();
              const diffSecs = Math.max(0, Math.round((now - start) / 1000));
              durationStr = diffSecs < 60 ? `Ativa agora (~${diffSecs}s)` : `Ativa agora (~${Math.round(diffSecs / 60)}m)`;
            } else if (ds != null && ds >= 0) {
              durationStr = ds < 60 ? `Duração: ${ds}s` : `Duração: ${Math.round(ds / 60)}m`;
            } else if (sess.end_time) {
              const start = new Date(sess.start_time);
              const end = new Date(sess.end_time);
              const diffSecs = Math.max(0, Math.round((end - start) / 1000));
              durationStr = diffSecs < 60 ? `Duração: ${diffSecs}s` : `Duração: ${Math.round(diffSecs / 60)}m`;
            } else {
              durationStr = "Duração: < 1m";
            }

            return (
              <div key={accessHistoryGrouped ? sess.user_id : (sess.session_id || i)} className="admin-ranking-item" style={{ alignItems: "flex-start" }}>
                <div className="admin-rank-number rank-default" style={{ fontSize: 14 }}>
                  <FontAwesomeIcon icon={faArrowRightToBracket} style={{ color: isOnline ? "#34c759" : "inherit" }} />
                </div>
                <div className="admin-rank-info">
                  <div className="admin-rank-name">
                    {sess.name}
                    {isOnline && <span style={{ marginLeft: 6, display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#34c759" }} title="Online agora" />}
                  </div>
                  <div className="admin-rank-stats" style={{ color: "#888", marginBottom: 4 }}>{sess.email}</div>
                  
                  {accessHistoryGrouped ? (
                    <div className="admin-rank-stats">
                      <strong>Última Sessão:</strong> {new Date(sess.start_time).toLocaleString("pt-BR")}
                      {sess.end_time && !isOnline && ` • Fim: ${new Date(sess.end_time).toLocaleTimeString("pt-BR")}`}
                      <span style={{ color: isOnline ? "#34c759" : "inherit", fontWeight: isOnline ? 600 : "normal" }}>
                        {` • ${durationStr}`}
                      </span>
                      <br/>
                      <div style={{ background: "rgba(212,175,55,.05)", padding: "8px 12px", borderRadius: 8, marginTop: 8, border: "1px solid rgba(212,175,55,.15)", display: "inline-block" }}>
                        <strong style={{ color: "#B8962E", display: "block", marginBottom: 4 }}>Histórico da Cliente:</strong>
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11 }}>
                          <span><strong>Acessos:</strong> {sess.total_sessions || 1} vezes</span>
                          <span><strong>Tempo Médio:</strong> {sess.total_sessions ? Math.round((sess.total_duration_secs || 0) / sess.total_sessions / 60) : 0}m</span>
                          <span><strong>Tempo Hoje:</strong> {Math.round((sess.today_duration_secs || 0) / 60)}m</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="admin-rank-stats">
                      <strong>Início:</strong> {new Date(sess.start_time).toLocaleString("pt-BR")}
                      {sess.end_time && !isOnline && ` • Fim: ${new Date(sess.end_time).toLocaleTimeString("pt-BR")}`}
                      <span style={{ color: isOnline ? "#34c759" : "inherit", fontWeight: isOnline ? 600 : "normal" }}>
                        {` • ${durationStr}`}
                      </span>
                      {sess.total_sessions > 1 && (
                        <span style={{ marginLeft: 8, color: "#B8962E", fontWeight: 600 }}>
                          (Já entrou {sess.total_sessions} vezes)
                        </span>
                      )}
                    </div>
                  )}

                  {sess.pages && sess.pages.length > 0 && !accessHistoryGrouped && (
                    <div className="admin-rank-stats" style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {sess.pages.map(p => (
                        <span key={p} style={{ background: "rgba(212,175,55,.1)", color: "#B8962E", padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600 }}>{p}</span>
                      ))}
                    </div>
                  )}
                </div>
                <span className="admin-rank-badge badge-iniciante" style={{ background: "rgba(0,0,0,.04)", color: "#888" }}>
                  {sess.device}
                </span>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const formatBrl = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  const renderBuyersTab = () => {
    if (buyersLoading && buyersList.length === 0) return renderLoading();
    return (
      <>
        <div className="admin-ranking-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
          <div>
            <h2>Clientes compradoras</h2>
            <p>
              {buyersTotal} no CRM (vendas/planilha CentraGF)
              {" · "}
              atualiza sozinha quando uma venda é registrada no CRM
            </p>
            {buyersError && (
              <p style={{ color: "#b45309", fontSize: 12, marginTop: 8 }}>{buyersError}</p>
            )}
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div className="admin-users-search" style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                placeholder="Buscar nome, telefone, email ou código..."
                value={buyersSearch}
                onChange={(e) => setBuyersSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadBuyers(0, buyersSearch)}
                style={{
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid rgba(212,175,55,0.3)",
                  outline: "none",
                  width: "280px",
                  fontSize: "13px",
                }}
              />
              <button
                type="button"
                onClick={() => loadBuyers(0, buyersSearch)}
                style={{
                  background: "linear-gradient(135deg, #D4AF37, #B8962E)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "0 16px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "13px",
                }}
              >
                Buscar
              </button>
              <button
                type="button"
                onClick={() => loadBuyers(buyersPage, buyersSearch)}
                title="Recarregar lista do CRM"
                style={{
                  border: "1px solid rgba(212,175,55,0.35)",
                  background: "#fff",
                  borderRadius: "8px",
                  padding: "0 12px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "12px",
                  color: "#8B6914",
                }}
              >
                Atualizar
              </button>
            </div>
            <div className="spin-pagination" style={{ marginTop: 0 }}>
              <button type="button" disabled={buyersPage === 0} onClick={() => loadBuyers(buyersPage - 1, buyersSearch)}>Anterior</button>
              <span>Página {buyersPage + 1}</span>
              <button type="button" disabled={(buyersPage + 1) * 20 >= buyersTotal} onClick={() => loadBuyers(buyersPage + 1, buyersSearch)}>Próxima</button>
            </div>
          </div>
        </div>

        <div className="admin-users-grid">
          {buyersList.map((b) => (
            <div key={b.clienteId ?? `${b.phone}-${b.name}`} className="admin-user-card">
              <div className="admin-user-card-header">
                <div>
                  <div className="admin-user-name">{b.name || "—"}</div>
                  <div className="admin-user-meta">{b.phone || b.email || "Sem contato"}</div>
                </div>
                {b.inApp ? (
                  <span className="admin-rank-badge badge-iniciante" style={{ background: "rgba(34,197,94,0.12)", color: "#15803d" }}>
                    No app
                  </span>
                ) : (
                  <span className="admin-rank-badge badge-iniciante" style={{ background: "rgba(0,0,0,.04)", color: "#888" }}>
                    Só CRM
                  </span>
                )}
              </div>
              <div className="admin-user-card-body" style={{ fontSize: 12, color: "#555", lineHeight: 1.5 }}>
                <div><strong>Compras:</strong> {b.purchasesCount ?? 0} · <strong>Total:</strong> {formatBrl(b.totalSpent)}</div>
                {b.tier && <div><strong>Tier:</strong> {b.tier}</div>}
                {b.customerCode && <div><strong>Código:</strong> {b.customerCode}</div>}
                {b.lastProduct && <div><strong>Última peça:</strong> {b.lastProduct}</div>}
                {b.lastPurchaseAt && <div><strong>Última compra:</strong> {formatGateDate(b.lastPurchaseAt) || b.lastPurchaseAt}</div>}
              </div>
              {b.appUserId && (
                <button
                  type="button"
                  className="admin-user-card-action"
                  onClick={() => openUserModal({ id: b.appUserId, name: b.name, phone: b.phone, email: b.email })}
                  style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: "#8B6914", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  Ver perfil no app →
                </button>
              )}
            </div>
          ))}
          {!buyersLoading && buyersList.length === 0 && !buyersError && (
            <p style={{ color: "#888", fontSize: 13 }}>Nenhuma compradora encontrada com esse filtro.</p>
          )}
        </div>
      </>
    );
  };

  const renderUsersTab = () => {
    if (usersLoading && usersList.length === 0) return renderLoading();
    const isGateView = usersSource === "gate";
    const gateStepLabel = GATE_STEP_LABELS;

    const switchUsersSource = (next) => {
      if (next === usersSource) return;
      setUsersSource(next);
      loadUsers(0, usersSearch, next);
    };

    return (
      <>
        <div className="admin-ranking-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
          <div>
            <h2>Base de Clientes</h2>
            <p>
              {isGateView
                ? `${usersTotal} leads da LP / Gate (não são cadastro no app)`
                : `${usersTotal} cadastradas no app${usersGateTotal ? ` · ${usersGateTotal} leads só da LP (aba ao lado)` : ""}`}
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button
                type="button"
                onClick={() => switchUsersSource("app")}
                style={{
                  padding: "6px 14px",
                  borderRadius: 8,
                  border: usersSource === "app" ? "1px solid #B8962E" : "1px solid rgba(212,175,55,0.25)",
                  background: usersSource === "app" ? "rgba(212,175,55,0.15)" : "#fff",
                  color: usersSource === "app" ? "#8B6914" : "#666",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 12,
                }}
              >
                App (cadastro real)
              </button>
              <button
                type="button"
                onClick={() => switchUsersSource("gate")}
                style={{
                  padding: "6px 14px",
                  borderRadius: 8,
                  border: usersSource === "gate" ? "1px solid #B8962E" : "1px solid rgba(212,175,55,0.25)",
                  background: usersSource === "gate" ? "rgba(212,175,55,0.15)" : "#fff",
                  color: usersSource === "gate" ? "#8B6914" : "#666",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 12,
                }}
              >
                Leads LP / Gate
              </button>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div className="admin-users-search" style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                placeholder={isGateView ? "Buscar por nome, email, telefone ou Instagram..." : "Buscar por nome, email ou telefone..."}
                value={usersSearch}
                onChange={(e) => setUsersSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadUsers(0, usersSearch, usersSource)}
                style={{
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid rgba(212,175,55,0.3)",
                  outline: "none",
                  width: "260px",
                  fontSize: "13px"
                }}
              />
              <button
                onClick={() => loadUsers(0, usersSearch, usersSource)}
                style={{
                  background: "linear-gradient(135deg, #D4AF37, #B8962E)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "0 16px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "13px"
                }}
              >
                Buscar
              </button>
            </div>
            <div className="spin-pagination" style={{ marginTop: 0 }}>
              <button disabled={usersPage === 0} onClick={() => loadUsers(usersPage - 1, usersSearch, usersSource)}>Anterior</button>
              <span>Página {usersPage + 1}</span>
              <button disabled={(usersPage + 1) * 20 >= usersTotal} onClick={() => loadUsers(usersPage + 1, usersSearch, usersSource)}>Próxima</button>
            </div>
          </div>
        </div>

        <div className="admin-users-grid">
          {usersList.map(u => (
            <div key={u.id} className="admin-user-card">
              <div className="admin-user-card-header">
                <div className="admin-user-avatar">{((isGateView ? u.name || u.email : u.name) || "?").charAt(0).toUpperCase()}</div>
                <div className="admin-user-title">
                  <h4>
                    {isGateView ? (u.name || "Sem nome ainda") : u.name}
                    {!isGateView && u.is_premium && <span title="Premium">👑</span>}
                  </h4>
                  <p>{u.email}</p>
                </div>
              </div>
              <div className="admin-user-card-body">
                {isGateView ? (
                  (() => {
                    const interest = u.interest || {};
                    const attr = u.attribution || {};
                    const device = u.device || {};
                    const ts = u.timestamps || {};
                    const handle = (u.instagram || u.instagram_handle || "").replace(/^@/, "");
                    const utmLine = [
                      attr.utm_source,
                      attr.utm_medium,
                      attr.utm_campaign,
                      attr.utm_content,
                      attr.utm_term,
                    ].filter(Boolean).join(" · ");
                    const inApp = gateInAppLabel(device.in_app);
                    const deviceLine = [
                      inApp,
                      device.device_os ? String(device.device_os).toUpperCase() : null,
                      device.is_ios === true ? "iOS" : device.is_android ? "Android" : null,
                      device.city && device.region ? `${device.city}/${device.region}` : device.city,
                    ].filter(Boolean).join(" · ");
                    const iphoneHint = device.iphone_models?.length
                      ? device.iphone_models.join(" / ")
                      : null;
                    return (
                      <>
                        <div
                          className="admin-user-detail"
                          style={{
                            background: "rgba(212,175,55,0.08)",
                            borderRadius: 8,
                            padding: "8px 10px",
                            marginBottom: 8,
                          }}
                        >
                          <strong>Interesse (Gate)</strong>
                          <div style={{ marginTop: 4 }}>
                            {interest.brand_label || interest.brand_id ? (
                              <div><strong>Marcas:</strong> {interest.brand_label || interest.brand_id}</div>
                            ) : (
                              <div style={{ color: "#888" }}>Marcas: não respondeu (funil curto ou incompleto)</div>
                            )}
                            {interest.owned_label || interest.owned_id ? (
                              <div><strong>Coleção:</strong> {interest.owned_label || interest.owned_id}</div>
                            ) : null}
                            {interest.ticket_label || interest.ticket ? (
                              <div><strong>Faixa R$ 4k:</strong> {interest.ticket_label || interest.ticket}</div>
                            ) : null}
                            {(interest.gate_category || interest.gate_style || interest.gate_budget || interest.gate_occasion) && (
                              <div style={{ marginTop: 4, fontSize: 12, color: "#555" }}>
                                {[interest.gate_category, interest.gate_style, interest.gate_budget, interest.gate_occasion]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="admin-user-detail">
                          <strong>Funil:</strong>{" "}
                          {[interest.funnel_label || interest.funnel_variant, interest.flow_label || interest.flow]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </div>
                        {interest.path && (
                          <div className="admin-user-detail"><strong>Página LP:</strong> {interest.path}</div>
                        )}
                        <div className="admin-user-detail">
                          <strong>Etapa:</strong> {gateStepLabel[u.step] || u.step || "—"}
                          {u.is_new === false ? " · lead recorrente" : u.is_new ? " · primeira vez" : ""}
                        </div>
                        <div className="admin-user-detail"><strong>Captura:</strong> {formatGateDate(ts.created_at || u.created_at)}</div>
                        {ts.profile_at && (
                          <div className="admin-user-detail"><strong>Perfil:</strong> {formatGateDate(ts.profile_at)}</div>
                        )}
                        {ts.group_clicked_at && (
                          <div className="admin-user-detail"><strong>Clique WPP/grupo:</strong> {formatGateDate(ts.group_clicked_at)}</div>
                        )}
                        {u.phone && (
                          <div className="admin-user-detail">
                            <strong>WhatsApp:</strong>{" "}
                            <a href={`https://wa.me/${u.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" style={{ color: "#B8962E", textDecoration: "none" }}>{u.phone}</a>
                          </div>
                        )}
                        {handle && (
                          <div className="admin-user-detail">
                            <strong>Instagram:</strong>{" "}
                            <a href={`https://instagram.com/${handle}`} target="_blank" rel="noreferrer" style={{ color: "#B8962E", textDecoration: "none" }}>@{handle}</a>
                          </div>
                        )}
                        {utmLine && (
                          <div className="admin-user-detail"><strong>UTM / ads:</strong> {utmLine}</div>
                        )}
                        {attr.fbclid && (
                          <div className="admin-user-detail" style={{ fontSize: 11, wordBreak: "break-all", color: "#666" }}>
                            <strong>fbclid:</strong> {String(attr.fbclid).slice(0, 48)}…
                          </div>
                        )}
                        {deviceLine && (
                          <div className="admin-user-detail"><strong>Dispositivo:</strong> {deviceLine}</div>
                        )}
                        {iphoneHint && (
                          <div className="admin-user-detail" style={{ fontSize: 12, color: "#666" }}>
                            <strong>iPhone provável:</strong> {iphoneHint}
                          </div>
                        )}
                        {u.joined_group && (
                          <div className="admin-user-detail"><strong>Grupo:</strong> clicou para entrar</div>
                        )}
                        <div className="admin-user-detail" style={{ color: "#888", fontSize: 12, marginTop: 8 }}>
                          Lead da landing — ainda não é cadastro no app. Se ela se cadastrar depois, os dados completam na aba App.
                        </div>
                      </>
                    );
                  })()
                ) : (
                  <>
                <div className="admin-user-detail"><strong>Cadastro:</strong> {new Date(u.created_at).toLocaleDateString("pt-BR")} às {new Date(u.created_at).toLocaleTimeString("pt-BR").slice(0,5)}</div>
                {u.phone && <div className="admin-user-detail"><strong>WhatsApp:</strong> <a href={`https://wa.me/${u.phone.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" style={{ color: "#B8962E", textDecoration: "none" }}>{u.phone}</a></div>}
                {u.instagram_handle && <div className="admin-user-detail"><strong>Instagram:</strong> <a href={`https://instagram.com/${u.instagram_handle.replace('@','')}`} target="_blank" rel="noreferrer" style={{ color: "#B8962E", textDecoration: "none" }}>{u.instagram_handle}</a></div>}
                {u.gender && <div className="admin-user-detail"><strong>Gênero:</strong> {u.gender}</div>}
                {u.birth_date && (() => {
                  const binfo = getBirthdayInfo(u.birth_date);
                  return (
                    <>
                      <div className="admin-user-detail">
                        <strong>Nascimento:</strong> {new Date(u.birth_date).toLocaleDateString("pt-BR", { timeZone: "UTC" })}
                        {binfo && <span className="admin-user-age"> · {binfo.age} anos</span>}
                      </div>
                      {binfo && (
                        <div className={`admin-user-birthday-badge ${binfo.isToday ? "today" : binfo.daysUntil <= 7 ? "soon" : binfo.daysUntil <= 30 ? "near" : ""}`}>
                          {binfo.isToday
                            ? "🎂 Aniversário HOJE!"
                            : `🎂 Aniversário em ${binfo.daysUntil} dia${binfo.daysUntil !== 1 ? "s" : ""}`}
                        </div>
                      )}
                    </>
                  );
                })()}

                {hasQuizPrefs(u.preferences) && (
                  <div className="admin-user-prefs">
                    <div className="admin-user-prefs-title">Preferências do Quiz:</div>
                    {quizBrands(u.preferences).length > 0 && <div className="admin-user-pref-item"><strong>Marcas:</strong> {quizBrands(u.preferences).join(", ")}</div>}
                    {u.preferences.produto_promo && <div className="admin-user-pref-item"><strong>Interesse:</strong> {u.preferences.produto_promo}</div>}
                    {u.preferences.selected_option && <div className="admin-user-pref-item"><strong>Já comprou?</strong> {u.preferences.selected_option}</div>}
                  </div>
                )}

                <div className="admin-user-card-footer">
                  <div className="admin-user-badges">
                    {u.points > 0 && (
                      <span className="admin-user-badge badge-points">
                        <FontAwesomeIcon icon={faStar} /> {u.points} pts
                      </span>
                    )}
                  </div>
                  <button className="admin-user-profile-btn" onClick={() => openUserModal(u)}>
                    Ver perfil completo
                  </button>
                </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderUserModal = () => {
    if (!selectedUserModal) return null;
    const u = userDetails?.user || selectedUserModal;
    const favorites = userDetails?.favorites || [];
    const surveys = userDetails?.surveys || [];
    const spinSurveys = userDetails?.spin_surveys || [];
    const pointsHistory = userDetails?.points_history || [];
    const activity = userDetails?.activity || {};
    const timeline = userDetails?.timeline || [];
    const gate = userDetails?.gate || {};
    const allPrefs = u.preferences || {};
    const gateAnswers = gate.answers && typeof gate.answers === "object" ? gate.answers : {};
    const hasGateAnswers = Object.keys(gateAnswers).length > 0;
    const surveysWithContent = surveys.filter((s) => s.has_content !== false && ((s.display_rows || []).length > 0 || Object.keys(s.answers || {}).length > 0));
    const spinWithContent = spinSurveys.filter((s) => s.has_content !== false && (s.question_won || s.question_wants));
    const hasAnySurvey = surveysWithContent.length > 0 || spinWithContent.length > 0 || hasGateAnswers;
    const surveyCount = surveysWithContent.length + spinWithContent.length + (hasGateAnswers ? 1 : 0);
    const timelineVisible = timeline.filter((ev) => ev.type !== "heartbeat");

    return (
      <div className="umodal-overlay" onClick={(e) => e.target === e.currentTarget && closeUserModal()}>
        <div className="umodal-container">
          <div className="umodal-header">
            <div className="umodal-header-left">
              <div className="umodal-avatar">{(u.name || "?").charAt(0).toUpperCase()}</div>
              <div>
                <h2 className="umodal-name">{u.name} {u.is_premium && <span title="Premium">👑</span>}</h2>
                <p className="umodal-email">{u.email}</p>
              </div>
            </div>
            <button className="umodal-close" onClick={closeUserModal}><FontAwesomeIcon icon={faXmark} /></button>
          </div>

          {userDetailsLoading && (
            <div className="umodal-loading">
              <FontAwesomeIcon icon={faSpinner} spin /> Carregando dados da cliente...
            </div>
          )}

          {!userDetailsLoading && userDetailsError && !userDetails && (
            <div className="umodal-loading" style={{ flexDirection: 'column', gap: 12 }}>
              <p style={{ color: '#e74c3c', margin: 0 }}>Erro ao carregar dados completos.</p>
              <button
                onClick={() => openUserModal(selectedUserModal)}
                style={{ background: '#D4AF37', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
              >
                Tentar novamente
              </button>
            </div>
          )}

          {!userDetailsLoading && (userDetails || !userDetailsError) && (
            <div className="umodal-body">
              {/* Bloco: Dados Básicos */}
              <div className="umodal-section">
                <div className="umodal-section-title">
                  <FontAwesomeIcon icon={faUsers} /> Dados Cadastrais
                </div>
                <div className="umodal-info-grid">
                  {u.phone && (
                    <div className="umodal-info-item">
                      <FontAwesomeIcon icon={faPhone} className="umodal-info-icon" />
                      <div>
                        <span className="umodal-info-label">WhatsApp</span>
                        <a href={`https://wa.me/${u.phone.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="umodal-info-value link">{u.phone}</a>
                      </div>
                    </div>
                  )}
                  {u.instagram_handle && (
                    <div className="umodal-info-item">
                      <FontAwesomeIcon icon={faTag} className="umodal-info-icon" />
                      <div>
                        <span className="umodal-info-label">Instagram</span>
                        <a href={`https://instagram.com/${u.instagram_handle.replace('@','')}`} target="_blank" rel="noreferrer" className="umodal-info-value link">{u.instagram_handle}</a>
                      </div>
                    </div>
                  )}
                  {u.gender && (
                    <div className="umodal-info-item">
                      <FontAwesomeIcon icon={faHeartPulse} className="umodal-info-icon" />
                      <div>
                        <span className="umodal-info-label">Gênero</span>
                        <span className="umodal-info-value">{u.gender}</span>
                      </div>
                    </div>
                  )}
                  {u.birth_date && (() => {
                    const binfo = getBirthdayInfo(u.birth_date);
                    return (
                      <div className="umodal-info-item">
                        <FontAwesomeIcon icon={faStar} className="umodal-info-icon" />
                        <div>
                          <span className="umodal-info-label">Nascimento</span>
                          <span className="umodal-info-value">
                            {new Date(u.birth_date).toLocaleDateString("pt-BR", { timeZone: "UTC" })}
                            {binfo && <> · <strong>{binfo.age} anos</strong></>}
                          </span>
                          {binfo && (
                            <span className={`umodal-birthday-tag ${binfo.isToday ? "today" : binfo.daysUntil <= 7 ? "soon" : binfo.daysUntil <= 30 ? "near" : ""}`}>
                              {binfo.isToday
                                ? "🎂 Aniversário HOJE!"
                                : `🎂 Em ${binfo.daysUntil} dia${binfo.daysUntil !== 1 ? "s" : ""}`}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="umodal-info-item">
                    <FontAwesomeIcon icon={faCoins} className="umodal-info-icon" />
                    <div>
                      <span className="umodal-info-label">Pontos</span>
                      <span className="umodal-info-value">{u.points || 0} pts</span>
                    </div>
                  </div>
                  <div className="umodal-info-item">
                    <FontAwesomeIcon icon={faClock} className="umodal-info-icon" />
                    <div>
                      <span className="umodal-info-label">Cadastro</span>
                      <span className="umodal-info-value">{new Date(u.created_at).toLocaleDateString("pt-BR")} às {new Date(u.created_at).toLocaleTimeString("pt-BR").slice(0,5)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="umodal-section">
                <div className="umodal-section-title">
                  <FontAwesomeIcon icon={faBolt} /> Tempo e toques
                </div>
                <div className="umodal-kpis">
                  <div className="umodal-kpi">
                    <span className="umodal-kpi-val">{activity.hours_label || "0s"}</span>
                    <span className="umodal-kpi-lbl">Horas no app</span>
                  </div>
                  <div className="umodal-kpi">
                    <span className="umodal-kpi-val">{activity.touches || 0}</span>
                    <span className="umodal-kpi-lbl">Toques</span>
                  </div>
                  <div className="umodal-kpi">
                    <span className="umodal-kpi-val">{activity.sessions || 0}</span>
                    <span className="umodal-kpi-lbl">Sessões no app</span>
                  </div>
                  <div className="umodal-kpi">
                    <span className="umodal-kpi-val">{activity.gate_enters || 0}</span>
                    <span className="umodal-kpi-lbl">Entradas na LP</span>
                  </div>
                  <div className="umodal-kpi">
                    <span className="umodal-kpi-val">{activity.photos || 0}</span>
                    <span className="umodal-kpi-lbl">Fotos / peças</span>
                  </div>
                  <div className="umodal-kpi">
                    <span className="umodal-kpi-val">{activity.cart_adds || 0}</span>
                    <span className="umodal-kpi-lbl">No carrinho</span>
                  </div>
                  <div className="umodal-kpi">
                    <span className="umodal-kpi-val">{activity.favorites || favorites.length || 0}</span>
                    <span className="umodal-kpi-lbl">Favoritou</span>
                  </div>
                  <div className="umodal-kpi">
                    <span className="umodal-kpi-val">{activity.buys || 0}</span>
                    <span className="umodal-kpi-lbl">Clicou comprar</span>
                  </div>
                </div>
                {(activity.first_seen_label || activity.last_seen_label) && (
                  <div className="umodal-prefs-grid" style={{ padding: "0 16px 14px" }}>
                    {activity.first_seen_label && (
                      <div className="umodal-pref-item">
                        <span className="umodal-pref-key">Primeiro evento</span>
                        <span className="umodal-pref-val">{activity.first_seen_label}</span>
                      </div>
                    )}
                    {activity.last_seen_label && (
                      <div className="umodal-pref-item">
                        <span className="umodal-pref-key">Último evento</span>
                        <span className="umodal-pref-val">{activity.last_seen_label}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {(activity.pages || []).length > 0 && (
                <div className="umodal-section">
                  <div className="umodal-section-title">
                    <FontAwesomeIcon icon={faEye} /> Páginas que visitou
                  </div>
                  <div className="umodal-pages">
                    {activity.pages.map((p) => (
                      <span key={p.page} className="umodal-page-chip">
                        {p.label || p.page} <em>{p.count}×</em>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {timeline.some((ev) => ev.type === "add_to_cart") && (
                <div className="umodal-section">
                  <div className="umodal-section-title">
                    <FontAwesomeIcon icon={faBagShopping} /> Carrinho
                  </div>
                  <div className="umodal-piece-grid">
                    {timeline.filter((ev) => ev.type === "add_to_cart").map((ev, i) => (
                      <div key={`cart-${ev.at}-${i}`} className="umodal-piece-card">
                        {ev.image_url ? <img src={ev.image_url} alt={ev.title || "Peça"} /> : <div className="umodal-tl-thumb is-empty" />}
                        <div>
                          <strong>{ev.title || "Peça"}</strong>
                          <span>{ev.at_label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="umodal-section">
                <div className="umodal-section-title">
                  <FontAwesomeIcon icon={faClock} /> Horários que entrou
                </div>
                {(activity.enter_hours || []).length === 0 ? (
                  <p className="umodal-empty">Ainda sem entrada registrada.</p>
                ) : (
                  <div className="umodal-enter-list">
                    {(activity.session_rows || []).map((row) => (
                      <div key={row.session_id} className="umodal-enter-item">
                        <span>{row.start_label}</span>
                        {row.is_gate ? (
                          <span>LP / Landing ({row.duration_label})</span>
                        ) : (
                          <span>{row.duration_label} · {(row.pages || []).map((p) => p === "/home" ? "Home" : p.replace("/", "")).join(", ") || "—"} · até {row.end_label}</span>
                        )}
                      </div>
                    ))}
                    {(activity.enter_hours || []).filter((h) => !(activity.session_rows || []).some((r) => r.start === h.at)).map((h) => (
                      <div key={h.at} className="umodal-enter-item">
                        <span>{h.label}</span>
                        <span>LP / Gate</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="umodal-section">
                <div className="umodal-section-title">
                  <FontAwesomeIcon icon={faClipboardList} /> Questionários ({surveyCount})
                </div>
                {!hasAnySurvey ? (
                  <p className="umodal-empty">Nenhum questionário enviado ainda.</p>
                ) : (
                  <div className="umodal-surveys-list">
                    {hasGateAnswers && (
                      <div className="umodal-survey-item">
                        <div className="umodal-survey-title">Quiz da landing</div>
                        {gate.entered_at_label && <div className="umodal-survey-date">{gate.entered_at_label}</div>}
                        <div className="umodal-survey-answers">
                          {Object.entries(gateAnswers).map(([k, v]) => (
                            <div key={k} className="umodal-survey-answer">
                              <strong>{String(k).replace(/_/g, " ")}:</strong> {Array.isArray(v) ? v.join(", ") : String(v)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {surveysWithContent.map((s, i) => {
                      const surveyTitle = s.survey_title
                        || (Array.isArray(s.surveys) ? s.surveys[0]?.title : s.surveys?.title)
                        || `Questionário #${i + 1}`;
                      const rows = (s.display_rows && s.display_rows.length > 0)
                        ? s.display_rows
                        : Object.entries(s.answers || {}).map(([k, v]) => ({
                            key: k,
                            label: k.replace(/_/g, " "),
                            value: Array.isArray(v) ? v.join(", ") : String(v),
                          }));
                      return (
                      <div key={s.survey_id || i} className="umodal-survey-item">
                        <div className="umodal-survey-title">{surveyTitle}</div>
                        <div className="umodal-survey-date">
                          {s.completed_at ? `${new Date(s.completed_at).toLocaleDateString("pt-BR")} às ${new Date(s.completed_at).toLocaleTimeString("pt-BR").slice(0, 5)}` : ""}
                        </div>
                        <div className="umodal-survey-answers">
                          {rows.map((row) => (
                            <div key={row.key} className="umodal-survey-answer">
                              <strong>{row.label}:</strong> {row.value}
                            </div>
                          ))}
                        </div>
                      </div>
                      );
                    })}
                    {spinWithContent.map((s, i) => (
                      <div key={`spin-${i}`} className="umodal-survey-item">
                        <div className="umodal-survey-title">Pesquisa da roleta</div>
                        <div className="umodal-survey-date">
                          {s.created_at ? `${new Date(s.created_at).toLocaleDateString("pt-BR")} às ${new Date(s.created_at).toLocaleTimeString("pt-BR").slice(0, 5)}` : ""}
                        </div>
                        <div className="umodal-survey-answers">
                          {s.question_won && <div className="umodal-survey-answer"><strong>ganhou:</strong> {s.question_won}</div>}
                          {s.question_wants && <div className="umodal-survey-answer"><strong>quer:</strong> {s.question_wants}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="umodal-section">
                <div className="umodal-section-title">
                  <FontAwesomeIcon icon={faEye} /> Todos os eventos ({timelineVisible.length})
                </div>
                {timelineVisible.length === 0 ? (
                  <p className="umodal-empty">Nenhum evento ainda.</p>
                ) : (
                  <div className="umodal-timeline">
                    {timelineVisible.map((ev, i) => {
                      const thumb = ev.image_url ? formatAuctionImageUrl(ev.image_url) : "";
                      const prettyTitle = ev.title || prettyStoryTitle(ev.image_url || ev.label);
                      const displayLabel = ev.label && !/T\d{2}:\d{2}/.test(ev.label)
                        ? ev.label
                        : (prettyTitle ? `Viu · ${prettyTitle}` : ev.label);
                      return (
                      <div key={`${ev.at}-${ev.type}-${i}`} className={`umodal-tl-item${ev.image_url ? " has-photo" : ""}`}>
                        <span className="umodal-tl-time">{ev.at_label || "—"}</span>
                        {thumb ? (
                          <img src={thumb} alt={prettyTitle || displayLabel} className="umodal-tl-thumb" />
                        ) : null}
                        <div>
                          <span className="umodal-tl-label">{displayLabel}</span>
                          <span className="umodal-tl-meta">
                            {ev.button_label ? <em>{ev.button_label}</em> : null}
                            {ev.page_label && ev.type !== "page_view" ? <span className="umodal-tl-page">{ev.page_label}</span> : null}
                          </span>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Bloco: Preferências do quiz (não o blob técnico do Gate) */}
              {hasQuizPrefs(allPrefs) && (
                <div className="umodal-section">
                  <div className="umodal-section-title">
                    <FontAwesomeIcon icon={faClipboardList} /> Preferências & Respostas
                  </div>
                  <div className="umodal-prefs-grid">
                    {quizBrands(allPrefs).length > 0 && (
                      <div className="umodal-pref-item">
                        <span className="umodal-pref-key">Marcas</span>
                        <span className="umodal-pref-val">{quizBrands(allPrefs).join(", ")}</span>
                      </div>
                    )}
                    {allPrefs.produto_promo && (
                      <div className="umodal-pref-item">
                        <span className="umodal-pref-key">Interesse</span>
                        <span className="umodal-pref-val">{allPrefs.produto_promo}</span>
                      </div>
                    )}
                    {allPrefs.selected_option && (
                      <div className="umodal-pref-item">
                        <span className="umodal-pref-key">Já comprou?</span>
                        <span className="umodal-pref-val">{allPrefs.selected_option}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bloco: Favoritos */}
              <div className="umodal-section">
                <div className="umodal-section-title">
                  <FontAwesomeIcon icon={faHeart} /> Favoritos ({favorites.length})
                </div>
                {favorites.length === 0 ? (
                  <p className="umodal-empty">Nenhum favorito ainda.</p>
                ) : (
                  <div className="umodal-favorites-grid">
                    {favorites.map((fav) => {
                      const imgSrc = fav.snapshot_image_url ? formatAuctionImageUrl(fav.snapshot_image_url) : "";
                      const title = fav.display_title || fav.snapshot_title || prettyStoryTitle(fav.snapshot_image_url) || "Favorito";
                      return (
                      <div key={fav.id} className="umodal-fav-card">
                        {imgSrc ? (
                          <img src={imgSrc} alt={title} className="umodal-fav-img" />
                        ) : (
                          <div className="umodal-fav-img-placeholder"><FontAwesomeIcon icon={faHeart} /></div>
                        )}
                        <div className="umodal-fav-info">
                          {fav.snapshot_brand && <span className="umodal-fav-brand">{fav.snapshot_brand}</span>}
                          <span className="umodal-fav-title">{title}</span>
                          {fav.snapshot_category && <span className="umodal-fav-cat">{fav.snapshot_category}</span>}
                          {fav.snapshot_original_price && <span className="umodal-fav-price">R$ {Number(fav.snapshot_original_price).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>}
                          {fav.snapshot_promo_title && <span className="umodal-fav-promo">{fav.snapshot_promo_title}</span>}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Bloco: Histórico de Pontos */}
              {pointsHistory.length > 0 && (
                <div className="umodal-section">
                  <div className="umodal-section-title">
                    <FontAwesomeIcon icon={faCoins} /> Histórico de Pontos
                  </div>
                  <div className="umodal-points-list">
                    {pointsHistory.map((tx, i) => (
                      <div key={i} className="umodal-points-item">
                        <div className="umodal-points-desc">{tx.description || tx.type}</div>
                        <div className="umodal-points-meta">
                          <span className={`umodal-points-val ${tx.points >= 0 ? "positive" : "negative"}`}>
                            {tx.points >= 0 ? "+" : ""}{tx.points} pts
                          </span>
                          <span className="umodal-points-date">{new Date(tx.created_at).toLocaleDateString("pt-BR")}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderInsights = () => {
    if (loading && !stats) return renderLoading();
    return (
      <>
        {renderOnlineBanner()}
        <div className="admin-insights-grid">
          {generateInsights().map((ins, i) => (
            <div key={i} className="admin-insight-card">
              <div className={`admin-insight-icon ${ins.type}`}><FontAwesomeIcon icon={ins.icon} /></div>
              <div className="admin-insight-content"><h3>{ins.title}</h3><p>{ins.desc}</p><div className="admin-insight-time">{ins.time}</div></div>
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderChat = () => (
    <div className="admin-chat-container">
      <div className="admin-chat-messages">
        {chatMessages.map((msg, i) => (
          <div key={i} className={`admin-chat-msg ${msg.from}`}>
            <div className={`admin-chat-avatar ${msg.from === "bot" ? "bot" : "human"}`}>{msg.from === "bot" ? <FontAwesomeIcon icon={faRobot} /> : "A"}</div>
            <div className="admin-chat-bubble">{msg.text}</div>
          </div>
        ))}
      </div>
      <div className="admin-chat-input-row">
        <input className="admin-chat-input" placeholder="Pergunte sobre os dados..." value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSendMessage()} />
        <button className="admin-chat-send" onClick={handleSendMessage}><FontAwesomeIcon icon={faPaperPlane} /></button>
      </div>
    </div>
  );

  const STATUS_LABEL = { pending: "Pendente", active: "Ativo", ended: "Encerrado", cancelled: "Cancelado" };
  const STATUS_CLASS = { pending: "pending", active: "active", ended: "ended", cancelled: "cancelled" };

  const renderAuctionForm = () => (
    <div className="auc-form-overlay" onClick={(e) => e.target === e.currentTarget && resetForm()}>
      <div className="auc-form-modal">
        <div className="auc-form-header">
          <h2>{editingAuction ? "Editar Leilão" : "Criar Novo Leilão"}</h2>
          <button className="auc-form-close" onClick={resetForm}><FontAwesomeIcon icon={faXmark} /></button>
        </div>
        <div className="auc-form-body">
          {!editingAuction && wishes.filter((w) => !w.seen).length > 0 && (
            <div className="auc-form-wishes-hint">
              <div className="auc-form-wishes-title"><FontAwesomeIcon icon={faLightbulb} style={{ color: "#D4AF37" }} /> As clientes querem:</div>
              <div className="auc-form-wishes-tags">
                {wishes.filter((w) => !w.seen).slice(0, 6).map((w) => (
                  <span key={w.id} className="auc-form-wish-tag" title={`${w.user_name}: ${w.message}`}>
                    {w.category} — {w.message.length > 40 ? w.message.slice(0, 40) + "…" : w.message}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="auc-field">
            <label>Título *</label>
            <input value={formData.title} onChange={(e) => handleFormChange("title", e.target.value)} placeholder="Ex: Bolsa Gucci Marmont" />
          </div>
          <div className="auc-field">
            <label>Descrição *</label>
            <textarea rows={3} value={formData.description} onChange={(e) => handleFormChange("description", e.target.value)} placeholder="Descrição detalhada do produto..." />
          </div>
          <div className="auc-field">
            <label><FontAwesomeIcon icon={faImages} style={{ marginRight: 6 }} />Imagens *</label>
            <p className="auc-field-hint" style={{ margin: "0 0 10px", fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>
              Governança: só fotos Instagram (luxemele) importadas para S3 ou upload manual. Yupoo/fornecedor proibido — ver <code>AUCTION_GOVERNANCE.md</code>.
            </p>
            <input
              ref={auctionFileRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={handleAuctionFileUpload}
            />
            <button
              type="button"
              className="auc-img-upload-btn"
              onClick={() => auctionFileRef.current?.click()}
              disabled={uploadingImages}
            >
              {uploadingImages ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Enviando...</>
              ) : (
                <><FontAwesomeIcon icon={faPlus} /> Fazer Upload de Fotos</>
              )}
            </button>
            {formData.images.filter((u) => u.trim()).length > 0 && (
              <div className="auc-img-preview-grid">
                {formData.images.filter((u) => u.trim()).map((img, idx) => (
                  <div key={idx} className="auc-img-preview-item">
                    <img src={img} alt={`Foto ${idx + 1}`} />
                    <button className="auc-img-preview-remove" onClick={() => removeImageField(formData.images.indexOf(img))}>
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: 6 }}>
              <small style={{ color: "#999", fontSize: 11 }}>Ou cole URLs manualmente:</small>
              {formData.images.map((img, idx) => (
                <div key={idx} className="auc-image-row" style={{ marginTop: 4 }}>
                  <input value={img} onChange={(e) => handleImageChange(idx, e.target.value)} placeholder="https://..." style={{ fontSize: 12 }} />
                  {formData.images.length > 1 && (
                    <button className="auc-img-remove" onClick={() => removeImageField(idx)}><FontAwesomeIcon icon={faTrash} /></button>
                  )}
                </div>
              ))}
              {formData.images.length < 10 && (
                <button className="auc-img-add" onClick={addImageField}><FontAwesomeIcon icon={faPlus} /> Adicionar URL</button>
              )}
            </div>
          </div>
          <div className="auc-row-2">
            <div className="auc-field">
              <label>Preço Inicial (R$) *</label>
              <input type="number" step="0.01" value={formData.initial_price} onChange={(e) => handleFormChange("initial_price", e.target.value)} placeholder="100.00" />
            </div>
            <div className="auc-field">
              <label>Incremento Mínimo (R$)</label>
              <input type="number" step="0.01" value={formData.increment} onChange={(e) => handleFormChange("increment", e.target.value)} placeholder="5.00" />
            </div>
          </div>
          <div className="auc-row-2">
            <div className="auc-field">
              <label>Início * <small style={{ color: "#999", fontWeight: 400 }}>(Horário de Brasília)</small></label>
              <input type="datetime-local" value={formData.start_time} onChange={(e) => handleFormChange("start_time", e.target.value)} />
            </div>
            <div className="auc-field">
              <label>Término * <small style={{ color: "#999", fontWeight: 400 }}>(Horário de Brasília)</small></label>
              <input type="datetime-local" value={formData.end_time} onChange={(e) => handleFormChange("end_time", e.target.value)} />
            </div>
          </div>
          <div className="auc-field">
            <label><FontAwesomeIcon icon={faTags} style={{ marginRight: 6 }} />Categoria *</label>
            <select value={formData.category} onChange={(e) => handleFormChange("category", e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Lances Automáticos */}
          <div className="auc-auto-bid-section" style={{ marginTop: 16, padding: 16, background: "rgba(212,175,55,.06)", borderRadius: 10, border: "1px solid rgba(212,175,55,.15)" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
              <input
                type="checkbox"
                checked={formData.auto_bid_enabled}
                onChange={(e) => handleFormChange("auto_bid_enabled", e.target.checked)}
                style={{ width: 18, height: 18, accentColor: "#D4AF37" }}
              />
              🤖 Ativar Lances Automáticos
            </label>
            {formData.auto_bid_enabled && (
              <>
                <div className="auc-row-2" style={{ marginTop: 8 }}>
                  <div className="auc-field">
                    <label>Preço Alvo / Venda (R$) *</label>
                    <input type="number" step="0.01" value={formData.target_price} onChange={(e) => handleFormChange("target_price", e.target.value)} placeholder="Ex: 500.00" />
                    <small style={{ color: "#999", fontSize: 11 }}>Lances fakes param ao atingir esse valor</small>
                  </div>
                </div>
                <div className="auc-row-2" style={{ marginTop: 8 }}>
                  <div className="auc-field">
                    <label>Intervalo Mín (min)</label>
                    <input type="number" value={formData.auto_bid_min_interval} onChange={(e) => handleFormChange("auto_bid_min_interval", e.target.value)} placeholder="2" />
                  </div>
                  <div className="auc-field">
                    <label>Intervalo Máx (min)</label>
                    <input type="number" value={formData.auto_bid_max_interval} onChange={(e) => handleFormChange("auto_bid_max_interval", e.target.value)} placeholder="8" />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        {editingAuction && (
          <div className="auc-field" style={{ marginTop: 12 }}>
            <label><strong>Status</strong></label>
            <select value={formData.status || editingAuction.status} onChange={(e) => handleFormChange("status", e.target.value)}>
              <option value="pending">Pendente</option>
              <option value="active">Ativo</option>
              <option value="ended">Encerrado</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </div>
        )}
        
        {/* SEÇÃO DE PRICING INTELIGENTE */}
        {/* SEÇÃO DO CATÁLOGO COMPOSER (CENTRAGF) E PRICING INTELIGENTE */}
        <div className="auc-composer-section">
          <div className="auc-composer-header">
            <div>
              <h3>
                <FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight: 8, color: "#D4AF37" }} />
                Catálogo Composer (CentraGF) & Preços Oficiais
              </h3>
              <small style={{ color: "#666" }}>
                Pesquise qualquer produto (ex: Marmont, Neverfull, Chanel, Saddle, Dior) para cruzar fotos reais do S3 com preços de vitrine, custo de catálogo e âncoras oficiais internacionais.
              </small>
            </div>
          </div>
          
          <div className="auc-field" style={{ position: "relative", marginTop: 12 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                placeholder="🔍 Digite para buscar no Composer (ex: Marmont, Neverfull, Classic, Lady Dior, Saddle...)"
                value={searchProducts}
                onChange={(e) => {
                  setSearchProducts(e.target.value);
                  searchProductsBySku(e.target.value);
                }}
                style={{ flex: 1, padding: "10px 14px", fontSize: "14px", borderRadius: 8, border: "1px solid #ccc" }}
              />
              {composerLoading && (
                <div style={{ display: "flex", alignItems: "center", padding: "0 12px", color: "#D4AF37" }}>
                  <FontAwesomeIcon icon={faSpinner} spin />
                </div>
              )}
            </div>

            {products.length > 0 && (
              <div className="auc-composer-dropdown">
                <div className="auc-composer-dropdown-header">
                  <span>Encontrados {products.length} itens no Composer:</span>
                  <button type="button" onClick={() => setProducts([])} style={{ border: "none", background: "none", cursor: "pointer", color: "#888" }}>✕</button>
                </div>
                {products.map((p, idx) => (
                  <div
                    key={p.sku || p.local_id || idx}
                    className="auc-composer-item"
                    onClick={() => {
                      handleSelectProduct(p);
                      setProducts([]);
                    }}
                  >
                    {p.image_thumb_url || p.image_url ? (
                      <img src={p.image_thumb_url || p.image_url} alt={p.title} className="auc-composer-thumb" />
                    ) : (
                      <div className="auc-composer-thumb-empty">Sem foto</div>
                    )}
                    <div className="auc-composer-info">
                      <div className="auc-composer-title-row">
                        <span className="auc-composer-brand-badge">{p.brand}</span>
                        <strong className="auc-composer-title">{p.title}</strong>
                        {p.category && <span className="auc-composer-cat-tag">{p.category}</span>}
                      </div>
                      
                      <div className="auc-composer-price-grid">
                        {p.store_price && (
                          <div className="auc-cp-col">
                            <span className="auc-cp-label">🏬 Loja Catálogo:</span>
                            <span className="auc-cp-val">R$ {Number(p.store_price).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {p.promo_price && (
                          <div className="auc-cp-col">
                            <span className="auc-cp-label">📦 Custo/Promo:</span>
                            <span className="auc-cp-val" style={{ color: "#28a745" }}>R$ {Number(p.promo_price).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {p.anchor && p.anchor.official_usd && (
                          <div className="auc-cp-col">
                            <span className="auc-cp-label">🇺🇸 Site Oficial EUA:</span>
                            <span className="auc-cp-val" style={{ color: "#0056b3" }}>
                              US$ {p.anchor.official_usd} (~R$ {Number(p.anchor.official_brl).toLocaleString("pt-BR", { minimumFractionDigits: 2 })})
                            </span>
                          </div>
                        )}
                        {p.anchor && p.anchor.farfetch_usd && (
                          <div className="auc-cp-col">
                            <span className="auc-cp-label">🇪🇺 Farfetch:</span>
                            <span className="auc-cp-val">US$ {p.anchor.farfetch_usd}</span>
                          </div>
                        )}
                      </div>

                      {p.pricing_recommendation && (
                        <div className="auc-composer-suggest-strip">
                          <span>💡 <strong>Sugestão Leilão:</strong> Inicial: <strong>R$ {Number(p.pricing_recommendation.suggested_initial_price).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</strong> | Alvo: <strong>R$ {Number(p.pricing_recommendation.suggested_target_price).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</strong></span>
                          <button type="button" className="auc-btn-use-composer">⚡ Preencher Leilão</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedProduct && (
            <div className="auc-selected-composer-card">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {selectedProduct.image_thumb_url || selectedProduct.image_url ? (
                  <img src={selectedProduct.image_thumb_url || selectedProduct.image_url} alt={selectedProduct.title} style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8, border: "1px solid #ddd" }} />
                ) : null}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#28a745" }} />
                    <strong>{selectedProduct.brand} - {selectedProduct.title}</strong>
                  </div>
                  <div style={{ fontSize: "12px", color: "#666", marginTop: 4 }}>
                    Preço Loja: <strong>R$ {Number(selectedProduct.store_price || selectedProduct.best_reference_price || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</strong> | Custo/Promo: <strong>R$ {Number(selectedProduct.our_cost || selectedProduct.promo_price || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</strong>
                    {selectedProduct.anchor && (
                      <span style={{ marginLeft: 8, color: "#0056b3" }}>
                        • Oficial {selectedProduct.brand}: US$ {selectedProduct.anchor.official_usd} (~R$ {Number(selectedProduct.anchor.official_brl).toLocaleString("pt-BR", { minimumFractionDigits: 2 })})
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedProduct(null)}
                  style={{ background: "none", border: "1px solid #ddd", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontSize: "12px", color: "#666" }}
                >
                  Limpar
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="auc-pricing-section">
          <h3><FontAwesomeIcon icon={faCalculator} style={{ marginRight: 8, color: "#D4AF37" }} />Pricing Inteligente & Regras de Lucro</h3>
          <small style={{ color: "#999", display: "block", marginBottom: 12 }}>
            Evita conflito: garante que cliente nunca pague mais no leilão que numa promoção comum
          </small>
          
          <div className="auc-row-2">
            <div className="auc-field">
              <label>Preço da Loja (R$)</label>
              <input 
                type="number" 
                step="0.01" 
                value={formData.store_price} 
                onChange={(e) => handleFormChange("store_price", e.target.value)} 
                placeholder="Ex: 26000.00" 
              />
              <small style={{ color: "#999", fontSize: 11 }}>Preço original (ex: R$ 26k Farfetch)</small>
            </div>
            <div className="auc-field">
              <label>Nome da Loja</label>
              <input 
                value={formData.store_name} 
                onChange={(e) => handleFormChange("store_name", e.target.value)} 
                placeholder="Ex: Farfetch, Saks, Matches" 
              />
            </div>
          </div>
          
          <div className="auc-row-2">
            <div className="auc-field">
              <label>Nosso Custo (R$)</label>
              <input 
                type="number" 
                step="0.01" 
                value={formData.our_cost} 
                onChange={(e) => handleFormChange("our_cost", e.target.value)} 
                placeholder="Ex: 16000.00" 
              />
              <small style={{ color: "#999", fontSize: 11 }}>Custo convertido para nós</small>
            </div>
            <div className="auc-field">
              <label>Desconto Máximo (%)</label>
              <input 
                type="number" 
                min="10" 
                max="90" 
                value={formData.max_discount_percent} 
                onChange={(e) => handleFormChange("max_discount_percent", e.target.value)} 
                placeholder="60" 
              />
              <small style={{ color: "#999", fontSize: 11 }}>Ex: 60% = cliente economiza 60%</small>
            </div>
          </div>
          
          {formData.store_price && formData.max_discount_percent && (
            <div className="auc-pricing-calc">
              <div className="auc-pricing-calc-row">
                <span>Preço mínimo seguro:</span>
                <strong style={{ color: "#D4AF37" }}>
                  R$ {(parseFloat(formData.store_price) * (100 - parseInt(formData.max_discount_percent)) / 100).toFixed(2)}
                </strong>
              </div>
              {formData.initial_price && (
                <div className="auc-pricing-calc-row">
                  <span>Status atual:</span>
                  <strong style={{ 
                    color: parseFloat(formData.initial_price) >= (parseFloat(formData.store_price) * (100 - parseInt(formData.max_discount_percent)) / 100) ? "#28a745" : "#dc3545" 
                  }}>
                    {parseFloat(formData.initial_price) >= (parseFloat(formData.store_price) * (100 - parseInt(formData.max_discount_percent)) / 100) ? "✓ SEGURO" : "⚠ RISCO"}
                  </strong>
                </div>
              )}
            </div>
          )}
          
          <div className="auc-field">
            <label>Observações de Pricing</label>
            <textarea 
              rows={2} 
              value={formData.pricing_notes} 
              onChange={(e) => handleFormChange("pricing_notes", e.target.value)} 
              placeholder="Ex: Verificar se temos promo ativa deste produto antes de ativar leilão"
            />
          </div>
        </div>
        
        <div className="auc-form-footer">
          <button className="auc-btn-cancel" onClick={resetForm}>Cancelar</button>
          <button className="auc-btn-save" onClick={handleSaveAuction}>{editingAuction ? "Salvar Alterações" : "Criar Leilão"}</button>
        </div>
      </div>
    </div>
  );

  const renderAuctions = () => (
    <>
      <div className="auc-toolbar">
        <div className="auc-filters">
          {["", "active", "pending", "ended", "cancelled"].map((f) => (
            <button key={f} className={`auc-filter-btn ${auctionFilter === f ? "active" : ""}`} onClick={() => setAuctionFilter(f)}>
              {f ? STATUS_LABEL[f] : "Todos"}
            </button>
          ))}
        </div>
        <button className="auc-btn-create" onClick={openCreateForm}><FontAwesomeIcon icon={faPlus} /> Novo Leilão</button>
      </div>

      {/* PAINEL DE CONTROLE DE DISPARO EM GRUPOS (DESATIVADO POR PADRÃO / DRY-RUN) */}
      <div className="auc-broadcast-banner">
        <div className="auc-broadcast-banner-left">
          <FontAwesomeIcon icon={faPaperPlane} style={{ color: broadcastConfig.enabled ? "#28a745" : "#888", fontSize: "16px" }} />
          <div>
            <strong>Disparo nos Grupos & Copilot:</strong>{" "}
            <span style={{ color: broadcastConfig.enabled ? "#28a745" : "#d97706", fontWeight: "bold" }}>
              {broadcastConfig.enabled ? "🟢 ATIVADO" : "🔒 DESATIVADO (Modo Seguro)"}
            </span>
            <div style={{ fontSize: "11px", color: "#666", marginTop: 2 }}>
              Destino de teste: <strong>{broadcastConfig.test_target || "pp 1522"}</strong> • Disparo real só ocorre se você habilitar.
            </div>
          </div>
        </div>
        <div className="auc-broadcast-banner-right">
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "12px", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={broadcastConfig.enabled}
              onChange={(e) => handleSaveBroadcastConfig({ enabled: e.target.checked })}
            />
            <span>Habilitar Disparo Real</span>
          </label>
        </div>
      </div>

      {/* PAINEL DO RADAR DE OPORTUNIDADES & COPILOT DE LEILÃO */}
      {radarData && radarData.opportunities?.length > 0 && (
        <div className="auc-radar-panel">
          <div className="auc-radar-header">
            <div className="auc-radar-title-group">
              <span className="auc-radar-icon">🎯</span>
              <div>
                <h3 className="auc-radar-title">Radar de Oportunidades & Recomendações do Copilot</h3>
                <p className="auc-radar-subtitle">
                  Clientes que pediram para comprar, adicionaram ao carrinho ou demonstraram desejo e sumiram. Lançar o leilão certo resgata essa demanda imediata!
                </p>
              </div>
            </div>
            <div className="auc-radar-badges">
              <span className="auc-radar-badge-sig">🔥 {radarData.summary?.total_signals} sinais capturados</span>
              <span className="auc-radar-badge-cli">👥 {radarData.summary?.unique_interested_clients} clientes esperando</span>
            </div>
          </div>

          <div className="auc-radar-grid">
            {radarData.opportunities.slice(0, 4).map((opp, idx) => (
              <div key={idx} className="auc-radar-card">
                <div className="auc-radar-card-top">
                  {opp.composer_match?.image_thumb_url || opp.composer_match?.image_url ? (
                    <img src={opp.composer_match.image_thumb_url || opp.composer_match.image_url} alt={opp.term} className="auc-radar-thumb" />
                  ) : (
                    <div className="auc-radar-thumb-placeholder">💎</div>
                  )}
                  <div className="auc-radar-card-info">
                    <span className="auc-radar-brand-tag">{opp.composer_match?.brand || opp.term.split(" ")[0]}</span>
                    <strong className="auc-radar-item-title">{opp.composer_match?.title || opp.term}</strong>
                    <div className="auc-radar-demand-line">
                      <span>🔥 <strong>{opp.unique_clients_count}</strong> cliente(s) no radar ({opp.interest_count} sinais)</span>
                    </div>
                  </div>
                </div>

                {opp.composer_match?.store_price && (
                  <div className="auc-radar-price-strip">
                    <span>Loja: R$ {Number(opp.composer_match.store_price).toLocaleString("pt-BR", { minimumFractionDigits: 0 })}</span>
                    <span style={{ color: "#28a745" }}>Custo: R$ {Number(opp.composer_match.our_cost || opp.composer_match.promo_price || 0).toLocaleString("pt-BR", { minimumFractionDigits: 0 })}</span>
                  </div>
                )}

                <div className="auc-radar-clients-sample">
                  <small>Interessadas: {opp.sample_clients?.slice(0, 2).join(", ") || "Clientes no radar"}</small>
                </div>

                <button
                  type="button"
                  className="auc-btn-radar-launch"
                  onClick={() => handleLaunchAuctionFromRadar(opp)}
                >
                  ⚡ Criar Leilão com 1 Clique
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PAINEL DE SUGESTÕES DAS CLIENTES */}
      {wishes.filter((w) => !w.seen).length > 0 && (
        <div className="auc-wishes-panel">
          <div className="auc-wishes-header">
            <FontAwesomeIcon icon={faLightbulb} style={{ color: "#D4AF37" }} />
            <span>Sugestões das Clientes</span>
            <span className="auc-wishes-badge">{wishes.filter((w) => !w.seen).length} novas</span>
          </div>
          <div className="auc-wishes-list">
            {wishes.filter((w) => !w.seen).slice(0, 10).map((w) => (
              <div key={w.id} className="auc-wish-card">
                <div className="auc-wish-top">
                  <span className="auc-wish-category">{w.category}</span>
                  <span className="auc-wish-date">{new Date(w.created_at).toLocaleDateString("pt-BR")}</span>
                </div>
                <div className="auc-wish-message">{w.message}</div>
                <div className="auc-wish-bottom">
                  <span className="auc-wish-user">{w.user_name || "Anônima"}{w.user_email ? ` • ${w.user_email}` : ""}</span>
                  <button className="auc-wish-seen-btn" onClick={() => handleMarkWishSeen(w.id)} title="Marcar como visto">
                    <FontAwesomeIcon icon={faEye} /> Visto
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {auctionLoading ? renderLoading() : auctions.length === 0 ? (
        <div className="auc-empty">
          <FontAwesomeIcon icon={faGavel} style={{ fontSize: 36, marginBottom: 12, opacity: 0.3 }} />
          <div>Nenhum leilão encontrado.</div>
          <button className="auc-btn-create" style={{ marginTop: 16 }} onClick={openCreateForm}><FontAwesomeIcon icon={faPlus} /> Criar Primeiro Leilão</button>
        </div>
      ) : (
        <div className="auc-list">
          {auctions.map((a) => (
            <div key={a.id} className={`auc-card status-${STATUS_CLASS[a.status] || "pending"}`}>
              <div className="auc-card-img">
                {a.images?.[0] ? <img src={formatAuctionImageUrl(a.images[0])} alt={a.title} /> : <div className="auc-card-noimg"><FontAwesomeIcon icon={faImages} /></div>}
                <span className={`auc-status-badge ${STATUS_CLASS[a.status]}`}>{STATUS_LABEL[a.status] || a.status}</span>
              </div>
              <div className="auc-card-body">
                <h3 className="auc-card-title">{a.title}</h3>
                <p className="auc-card-desc">{a.description?.slice(0, 100)}{a.description?.length > 100 ? "..." : ""}</p>
                {a.store_price > 0 && (
                  <div className="auc-store-ref-row">
                    <span className="auc-store-ref-label">Valor da loja</span>
                    <span className="auc-store-ref-price">
                      R$ {Number(a.store_price).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      {a.store_price_usd > 0 && (
                        <span className="auc-store-ref-usd">
                          (${Number(a.store_price_usd).toLocaleString("en-US", { maximumFractionDigits: 0 })} USD)
                        </span>
                      )}
                    </span>
                    {a.official_brand && <span className="auc-store-ref-brand">{a.official_brand}</span>}
                    {a.discount_percent > 0 && (
                      <span className="auc-store-ref-discount">
                        -{a.discount_percent}% OFF
                        {a.savings_amount > 0 && (
                          <span className="auc-store-ref-savings">
                            · Economia R$ {Number(a.savings_amount).toLocaleString("pt-BR", { minimumFractionDigits: 0 })}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                )}
                <div className="auc-card-meta">
                  <span className="auc-meta-item"><strong>Preço inicial:</strong> R$ {Number(a.initial_price).toFixed(2)}</span>
                  <span className="auc-meta-item"><strong>Preço atual:</strong> R$ {Number(a.current_price).toFixed(2)}</span>
                  <span className="auc-meta-item"><strong>Lances:</strong> {a.total_bids || 0}</span>
                  <span className="auc-meta-item"><strong>Incremento:</strong> R$ {Number(a.increment || 5).toFixed(2)}</span>
                  <span className="auc-meta-item"><strong>Categoria:</strong> {a.category}</span>
                  {a.target_price && <span className="auc-meta-item"><strong>Alvo:</strong> R$ {Number(a.target_price).toFixed(2)}</span>}
                  {a.auto_bid_enabled && <span className="auc-meta-item" style={{ color: "#D4AF37" }}>🤖 Auto-Bid ON ({a.auto_bid_min_interval}-{a.auto_bid_max_interval}min)</span>}
                </div>
                <div className="auc-card-dates">
                  <span>Início: {a.start_time ? new Date(a.start_time).toLocaleString("pt-BR") : "—"}</span>
                  <span>Fim: {a.end_time ? new Date(a.end_time).toLocaleString("pt-BR") : "—"}</span>
                </div>
                {a.winner_name && <div className="auc-winner">Vencedor: {a.winner_name}</div>}
                <button
                  className={`auc-bids-toggle ${expandedBids === a.id ? "active" : ""}`}
                  onClick={() => toggleBidsPanel(a.id)}
                >
                  <FontAwesomeIcon icon={faEye} /> {expandedBids === a.id ? "Fechar Lances" : "Ver Lances"}
                </button>
                {expandedBids === a.id && (
                  <div className="auc-bids-panel">
                    <div className="auc-bids-panel-header">
                      <strong>Lances em Tempo Real</strong>
                      <span className="auc-bids-panel-dot" />
                    </div>
                    {bidsLoading ? (
                      <div style={{ textAlign: "center", padding: 16, color: "#999" }}><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>
                    ) : auctionBids.length === 0 ? (
                      <div style={{ textAlign: "center", padding: 16, color: "#999" }}>Nenhum lance ainda</div>
                    ) : (
                      <div className="auc-bids-list">
                        {auctionBids.map((b, idx) => (
                          <div key={b.id || idx} className={`auc-bid-row ${b.is_fake ? "fake" : "real"}`}>
                            <div className="auc-bid-rank">{idx === 0 ? "🏆" : `#${idx + 1}`}</div>
                            <div className="auc-bid-info">
                              <span className="auc-bid-name">
                                {b.user_name}
                                {b.is_fake && <span className="auc-bid-fake-tag">FAKE</span>}
                                {!b.is_fake && b.user_id && <span className="auc-bid-real-tag">REAL</span>}
                              </span>
                              <span className="auc-bid-time">{new Date(b.timestamp || b.created_at).toLocaleString("pt-BR")}</span>
                            </div>
                            <div className="auc-bid-amount">R$ {Number(b.amount).toFixed(2)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="auc-card-actions">
                <button className="auc-action-btn edit" onClick={() => openEditForm(a)} title="Editar"><FontAwesomeIcon icon={faPen} /></button>
                <button className="auc-action-btn radar" onClick={() => handleOpenRadarMatch(a)} title="Ver Clientes no Radar para esta peça" style={{ background: "rgba(212,175,55,.15)", color: "#8B6914" }}>
                  <FontAwesomeIcon icon={faLightbulb} />
                </button>
                <button className="auc-action-btn broadcast" onClick={() => handlePreviewBroadcast(a)} title="Prévia da Mensagem & Teste pp 1522" style={{ background: "rgba(0,123,255,.1)", color: "#007bff" }}>
                  <FontAwesomeIcon icon={faPaperPlane} />
                </button>
                {a.status === "pending" && (
                  <span className="auc-meta-item" style={{ color: "#8B6914", fontSize: 12 }} title="Abre sozinho no start_time">
                    ⏱ Abre automaticamente no horário
                  </span>
                )}
                {(a.status === "ended" || a.status === "cancelled") && (
                  <button className="auc-action-btn activate" onClick={() => handleAuctionAction(a.id, "activate")} title="Reativar"><FontAwesomeIcon icon={faPlay} /></button>
                )}
                {a.status === "active" && (
                  <button className="auc-action-btn end" onClick={() => handleAuctionAction(a.id, "end")} title="Encerrar"><FontAwesomeIcon icon={faStop} /></button>
                )}
                {(a.status === "pending" || a.status === "active") && (
                  <button className="auc-action-btn cancel" onClick={() => handleAuctionAction(a.id, "cancel")} title="Cancelar"><FontAwesomeIcon icon={faBan} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL DE PREVIEW E TRANSMISSÃO MULTI-CANAL */}
      {broadcastModal && (
        <div className="auc-modal-overlay" onClick={() => setBroadcastModal(null)}>
          <div className="auc-broadcast-modal auc-broadcast-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="auc-broadcast-modal-header">
              <div>
                <h3><FontAwesomeIcon icon={faPaperPlane} style={{ color: "#007bff", marginRight: 8 }} />Central de Disparos & Broadcast Multi-Canal</h3>
                <small style={{ color: "#666" }}>Peça: <strong>{broadcastModal.auction?.title}</strong> · Lance Atual: <strong>R$ {Number(broadcastModal.auction?.current_price || broadcastModal.auction?.initial_price).toFixed(2)}</strong></small>
              </div>
              <button className="auc-btn-close-modal" onClick={() => setBroadcastModal(null)}>✕</button>
            </div>

            <div className="auc-broadcast-modal-body">
              {/* Resumo de Audiência e Radar */}
              <div className="auc-bcast-audience-grid">
                <div className="auc-bcast-aud-card">
                  <span className="aud-val">🎯 {broadcastModal.audience?.radar_target_clients || 0}</span>
                  <span className="aud-lbl">Clientes no Radar</span>
                  <small>Carrinho abandonado / Desejos</small>
                </div>
                <div className="auc-bcast-aud-card">
                  <span className="aud-val">👥 {broadcastModal.audience?.wpp_groups_count || 1}</span>
                  <span className="aud-lbl">Grupo VIP WhatsApp</span>
                  <small>Comunidade de Clientes</small>
                </div>
                <div className="auc-bcast-aud-card">
                  <span className="aud-val">📱 {broadcastModal.audience?.sms_estimated_vip_leads || 0}</span>
                  <span className="aud-lbl">Leads VIP SMS</span>
                  <small>Alta taxa de abertura</small>
                </div>
                <div className="auc-bcast-aud-card">
                  <span className="aud-val">🔔 {broadcastModal.audience?.pwa_push_subscribers || 0}</span>
                  <span className="aud-lbl">Push PWA</span>
                  <small>Notificações no celular</small>
                </div>
              </div>

              {/* Seletor de Canais para Disparo */}
              <div className="auc-bcast-channels-selector">
                <span className="auc-bcast-sec-title">Canais Ativos para o Disparo:</span>
                <div className="auc-bcast-chk-group">
                  <label className={`auc-channel-chip ${selectedBroadcastChannels.includes("wpp_group") ? "active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={selectedBroadcastChannels.includes("wpp_group")}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedBroadcastChannels((p) => [...p, "wpp_group"]);
                        else setSelectedBroadcastChannels((p) => p.filter((c) => c !== "wpp_group"));
                      }}
                    />
                    <span>🟢 Grupo WhatsApp</span>
                  </label>

                  <label className={`auc-channel-chip ${selectedBroadcastChannels.includes("sms") ? "active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={selectedBroadcastChannels.includes("sms")}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedBroadcastChannels((p) => [...p, "sms"]);
                        else setSelectedBroadcastChannels((p) => p.filter((c) => c !== "sms"));
                      }}
                    />
                    <span>📱 SMS VIP</span>
                  </label>

                  <label className={`auc-channel-chip ${selectedBroadcastChannels.includes("wpp_official") ? "active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={selectedBroadcastChannels.includes("wpp_official")}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedBroadcastChannels((p) => [...p, "wpp_official"]);
                        else setSelectedBroadcastChannels((p) => p.filter((c) => c !== "wpp_official"));
                      }}
                    />
                    <span>💬 WhatsApp Oficial API (1:1)</span>
                  </label>

                  <label className={`auc-channel-chip ${selectedBroadcastChannels.includes("pwa_push") ? "active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={selectedBroadcastChannels.includes("pwa_push")}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedBroadcastChannels((p) => [...p, "pwa_push"]);
                        else setSelectedBroadcastChannels((p) => p.filter((c) => c !== "pwa_push"));
                      }}
                    />
                    <span>🔔 Push PWA</span>
                  </label>
                </div>
              </div>

              {/* Abas de Visualização dos Formatos */}
              <div className="auc-bcast-tabs">
                <button
                  type="button"
                  className={`auc-bcast-tab-btn ${broadcastTab === "wpp_group" ? "active" : ""}`}
                  onClick={() => setBroadcastTab("wpp_group")}
                >
                  🟢 Grupo WhatsApp
                </button>
                <button
                  type="button"
                  className={`auc-bcast-tab-btn ${broadcastTab === "sms" ? "active" : ""}`}
                  onClick={() => setBroadcastTab("sms")}
                >
                  📱 SMS
                </button>
                <button
                  type="button"
                  className={`auc-bcast-tab-btn ${broadcastTab === "wpp_official" ? "active" : ""}`}
                  onClick={() => setBroadcastTab("wpp_official")}
                >
                  💬 WhatsApp Oficial (Meta Cloud)
                </button>
                <button
                  type="button"
                  className={`auc-bcast-tab-btn ${broadcastTab === "pwa_push" ? "active" : ""}`}
                  onClick={() => setBroadcastTab("pwa_push")}
                >
                  🔔 Notificação Push
                </button>
              </div>

              {/* Conteúdo da Aba Ativa */}
              <div className="auc-bcast-tab-content">
                {broadcastTab === "wpp_group" && (
                  <div className="auc-broadcast-preview-box">
                    <div className="auc-preview-phone-header">
                      <span>📱 Formato para Grupos WhatsApp & Comunidades</span>
                      <span className="auc-preview-dest">Destino de teste: <strong>{broadcastConfig.test_target || "pp 1522"}</strong></span>
                    </div>
                    {broadcastModal.payloads?.wpp_group?.image_url && (
                      <img src={formatAuctionImageUrl(broadcastModal.payloads.wpp_group.image_url)} alt="Imagem do Leilão" className="auc-preview-img" />
                    )}
                    <pre className="auc-preview-text">{broadcastModal.payloads?.wpp_group?.text || broadcastModal.preview?.text}</pre>
                  </div>
                )}

                {broadcastTab === "sms" && (
                  <div className="auc-broadcast-preview-box sms-view">
                    <div className="auc-preview-phone-header">
                      <span>📱 SMS Marketing VIP (Direto no Celular)</span>
                      <span className="auc-preview-dest">Tamanho: <strong>{broadcastModal.payloads?.sms?.char_count || 0}/160 caracteres</strong></span>
                    </div>
                    <div className="auc-sms-bubble">
                      <p>{broadcastModal.payloads?.sms?.text}</p>
                    </div>
                  </div>
                )}

                {broadcastTab === "wpp_official" && (
                  <div className="auc-broadcast-preview-box wpp-official-view">
                    <div className="auc-preview-phone-header">
                      <span>💬 Template Interativo WhatsApp Cloud API (1:1 com Lead)</span>
                      <span className="auc-preview-dest">Template: <strong>{broadcastModal.payloads?.wpp_official?.template?.name || "auction_live_alert_v1"}</strong></span>
                    </div>
                    <div className="auc-wpp-card">
                      {broadcastModal.payloads?.wpp_official?.template?.components?.[0]?.parameters?.[0]?.image?.link && (
                        <img src={broadcastModal.payloads.wpp_official.template.components[0].parameters[0].image.link} alt="Bolsa" className="auc-preview-img" />
                      )}
                      <div className="auc-wpp-card-body">
                        <pre className="auc-preview-text">{broadcastModal.payloads?.wpp_official?.preview_text}</pre>
                        <div className="auc-wpp-quick-buttons">
                          <button type="button" className="auc-wpp-btn">⚡ Dar Lance Agora</button>
                          <button type="button" className="auc-wpp-btn">👜 Ver Fotos e Detalhes</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {broadcastTab === "pwa_push" && (
                  <div className="auc-broadcast-preview-box push-view">
                    <div className="auc-preview-phone-header">
                      <span>🔔 Push Notification PWA (Tela de Bloqueio)</span>
                    </div>
                    <div className="auc-push-card">
                      <div className="auc-push-header">
                        <span className="auc-push-app">Sua Personal Shopper</span>
                        <span className="auc-push-time">agora</span>
                      </div>
                      <div className="auc-push-title">{broadcastModal.payloads?.pwa_push?.title}</div>
                      <div className="auc-push-body">{broadcastModal.payloads?.pwa_push?.body}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Status do Disparo Global & Configuração */}
              <div className="auc-broadcast-status-box">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <strong>Switch Mestre de Disparos Oficiais:</strong>{" "}
                    <span style={{ color: broadcastConfig.enabled ? "#28a745" : "#d97706", fontWeight: "bold" }}>
                      {broadcastConfig.enabled ? "🟢 Habilitado (Envio Real Ativo)" : "🔒 Desativado (Modo Simulação & Testes Isolados)"}
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: "11px", color: "#666", marginTop: 4 }}>
                  Para proteção contra envios acidentais, disparos para toda a base exigem o switch ligado. No modo Simulação, você pode testar à vontade sem disparar a clientes reais.
                </div>
              </div>

              {/* Logs da Execução Recente */}
              {broadcastModal.dispatchResult && (
                <div className="auc-bcast-result-log">
                  <div className="auc-bcast-log-header">
                    <strong>📋 Resultado do Disparo ({broadcastModal.dispatchResult.mode}):</strong>
                    <span className={`status-pill ${broadcastModal.dispatchResult.status}`}>
                      {broadcastModal.dispatchResult.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="auc-bcast-log-details">
                    {Object.entries(broadcastModal.dispatchResult.channel_logs || {}).map(([chan, log]) => (
                      <div key={chan} className="auc-bcast-log-row">
                        <strong>[{chan.toUpperCase()}]:</strong> {log.status} · {log.destination || (log.recipients_count ? `${log.recipients_count} destinatários` : "") || log.template || "OK"}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="auc-broadcast-modal-footer">
              <button className="auc-btn-cancel" onClick={() => setBroadcastModal(null)}>Fechar</button>
              
              <button
                type="button"
                className="auc-btn-simulate"
                onClick={() => handleDispatchBroadcast("simulate")}
                disabled={broadcastLoading}
                title="Executa simulação de cálculo e geração de mensagens sem disparar"
              >
                {broadcastLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : "🧪"}
                <span>Testar Simulação (Dry-Run)</span>
              </button>

              <button
                type="button"
                className="auc-btn-test-send"
                onClick={() => handleDispatchBroadcast("test")}
                disabled={broadcastLoading}
              >
                {broadcastLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPaperPlane} />}
                <span>Disparar Teste para "{broadcastConfig.test_target || "pp 1522"}"</span>
              </button>

              {broadcastConfig.enabled && (
                <button
                  type="button"
                  className="auc-btn-live-send"
                  onClick={() => handleDispatchBroadcast("live")}
                  disabled={broadcastLoading}
                  style={{ background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
                >
                  🚀 Disparar para Todos
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL DO RADAR DE MATCH DE CLIENTES */}
      {radarMatchModal && (
        <div className="auc-modal-overlay" onClick={() => setRadarMatchModal(null)}>
          <div className="auc-broadcast-modal" onClick={(e) => e.stopPropagation()}>
            <div className="auc-broadcast-modal-header">
              <div>
                <h3><FontAwesomeIcon icon={faLightbulb} style={{ color: "#D4AF37", marginRight: 8 }} />Clientes no Radar para este Leilão</h3>
                <small style={{ color: "#666" }}>Peça: <strong>{radarMatchModal.auction.title}</strong></small>
              </div>
              <button className="auc-btn-close-modal" onClick={() => setRadarMatchModal(null)}>✕</button>
            </div>

            <div className="auc-broadcast-modal-body">
              {radarMatchModal.matchData?.matched_opportunities?.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 0", color: "#666" }}>
                  <p>Nenhuma cliente com intenção prévia direta registrada para este termo específico.</p>
                  <small>Quando clientes adicionarem itens similares ao carrinho ou pedirem no Gênio, o Copilot listará aqui.</small>
                </div>
              ) : (
                <div>
                  <div style={{ background: "rgba(212,175,55,.1)", border: "1px solid rgba(212,175,55,.3)", borderRadius: 8, padding: 12, marginBottom: 14 }}>
                    <strong style={{ color: "#8B6914" }}>🎯 {radarMatchModal.matchData?.target_clients_count} cliente(s) no Radar com interesse:</strong>
                    <div style={{ fontSize: "12px", color: "#555", marginTop: 4 }}>
                      Estas clientes manifestaram intenção de compra ou adicionaram peças da mesma linha/marca e sumiram.
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {radarMatchModal.matchData?.matched_opportunities?.map((opp, idx) => (
                      <div key={idx} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <strong>{opp.term}</strong>
                          <span style={{ fontSize: "11px", background: "#e2e8f0", padding: "2px 8px", borderRadius: 12 }}>
                            {opp.interest_count} sinais
                          </span>
                        </div>
                        <div style={{ fontSize: "12px", color: "#666", marginTop: 6 }}>
                          {opp.reason}
                        </div>
                        {opp.sample_clients?.length > 0 && (
                          <div style={{ marginTop: 8, fontSize: "12px", color: "#333" }}>
                            <strong>Contatos no Radar:</strong>
                            <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                              {opp.sample_clients.map((c, cIdx) => (
                                <li key={cIdx}>{c}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="auc-broadcast-modal-footer">
              <button className="auc-btn-cancel" onClick={() => setRadarMatchModal(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {showForm && renderAuctionForm()}
    </>
  );

  const content = { metrics: renderMetrics, users: renderUsersTab, buyers: renderBuyersTab, ranking: renderRanking, access: renderAccessHistory, insights: renderInsights, chat: renderChat, auctions: renderAuctions, promos: () => <PromoManagement />, spins: () => <SpinManagement />, surveys: () => <SurveysColeta />, interests: () => <ClientInterestsManagement />, favorites: () => <FavoritesManagement openUserModal={openUserModal} />, carts: () => <CartRadarManagement />, testimonials: () => <TestimonialsManagement />, photos: () => <PhotosManagement /> };

  return (
    <div className="admin-root">
      <div className="admin-shell">
        <div className="admin-header">
          <div className="admin-header-icon"><FontAwesomeIcon icon={faRobot} /></div>
          <div className="admin-header-text"><h1>Agent de Dados do App IA</h1><p>Inteligência artificial analisando métricas e comportamento 24/7</p></div>
        </div>
        <nav className="admin-tabs">
          {TABS.map((tab) => (
            <button key={tab.key} className={`admin-tab ${activeTab === tab.key ? "active" : ""}`} onClick={() => setActiveTab(tab.key)}>
              <FontAwesomeIcon icon={tab.icon} />{tab.label}
            </button>
          ))}
        </nav>
        {content[activeTab]()}
      </div>
      {renderUserModal()}
    </div>
  );
};

// ─── Gestão de Fotos ───────────────────────────────────────────────────────
function PhotosManagement() {
  const [images, setImages] = useState({ bags: [], variados: [] });
  const [loading, setLoading] = useState(true);
  const [folder, setFolder] = useState("bags");
  const [search, setSearch] = useState("");
  const [toggling, setToggling] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({ bags: 0, variados: 0, disabledBags: 0, disabledVariados: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const data = await getAdminImages();
      setImages(data);
      setStats({
        bags: data.bags?.length || 0,
        variados: data.variados?.length || 0,
        disabledBags: data.bags?.filter(i => i.disabled).length || 0,
        disabledVariados: data.variados?.filter(i => i.disabled).length || 0,
      });
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (url) => {
    setToggling(url);
    try {
      await toggleAdminImage(url, folder);
      setImages(prev => ({
        ...prev,
        [folder]: prev[folder].map(img =>
          img.url === url ? { ...img, disabled: !img.disabled } : img
        ),
      }));
      setStats(prev => {
        const key = folder === "bags" ? "disabledBags" : "disabledVariados";
        const item = images[folder].find(i => i.url === url);
        return { ...prev, [key]: item?.disabled ? prev[key] - 1 : prev[key] + 1 };
      });
    } catch (e) {
      console.error(e);
    }
    setToggling(null);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshImagesCache();
      await load();
    } catch (e) {
      console.error(e);
    }
    setRefreshing(false);
  };

  const currentList = (images[folder] || []).filter(img => {
    if (!search) return true;
    const name = img.url.split("/").pop().toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const s = { card: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(212,175,55,0.15)", borderRadius: 14, padding: "24px", marginBottom: 20 }, title: { color: "#d4a017", fontWeight: 700, fontSize: 16, marginBottom: 16 }, statBox: { display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }, stat: { background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.2)", borderRadius: 10, padding: "12px 20px", flex: 1, minWidth: 130, textAlign: "center" }, statNum: { color: "#d4a017", fontWeight: 800, fontSize: 22 }, statLabel: { color: "#aaa", fontSize: 11, marginTop: 4 }, controls: { display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }, btn: { background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, padding: "8px 16px", color: "#d4a017", cursor: "pointer", fontSize: 13, fontWeight: 600 }, btnActive: { background: "rgba(212,175,55,0.3)", color: "#fff" }, search: { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(212,175,55,0.2)", borderRadius: 8, padding: "8px 14px", color: "#fff", fontSize: 13, flex: 1, minWidth: 200 }, grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10, maxHeight: 600, overflowY: "auto" }, imgCard: { position: "relative", borderRadius: 10, overflow: "hidden", border: "2px solid transparent", cursor: "pointer", transition: "all 0.2s" }, imgCardDisabled: { border: "2px solid #e74c3c", opacity: 0.5 }, imgCardActive: { border: "2px solid rgba(212,175,55,0.4)" }, img: { width: "100%", aspectRatio: "1/1", objectFit: "cover", display: "block" }, badge: { position: "absolute", top: 4, right: 4, background: "#e74c3c", color: "#fff", borderRadius: 6, padding: "2px 6px", fontSize: 10, fontWeight: 700 }, badgeActive: { background: "rgba(46,204,113,0.85)" }, overlay: { position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s" }, overlayText: { color: "#fff", fontSize: 11, fontWeight: 700, textAlign: "center" } };

  return (
    <div style={{ padding: "0 4px" }}>
      <div style={s.card}>
        <div style={s.title}>Gestão de Fotos em Tempo Real</div>
        <div style={s.statBox}>
          <div style={s.stat}><div style={s.statNum}>{stats.bags}</div><div style={s.statLabel}>Total Bolsas</div></div>
          <div style={s.stat}><div style={{ ...s.statNum, color: "#e74c3c" }}>{stats.disabledBags}</div><div style={s.statLabel}>Bolsas Desativadas</div></div>
          <div style={s.stat}><div style={s.statNum}>{stats.variados}</div><div style={s.statLabel}>Total Variados</div></div>
          <div style={s.stat}><div style={{ ...s.statNum, color: "#e74c3c" }}>{stats.disabledVariados}</div><div style={s.statLabel}>Variados Desativados</div></div>
        </div>
        <div style={s.controls}>
          <button style={{ ...s.btn, ...(folder === "bags" ? s.btnActive : {}) }} onClick={() => setFolder("bags")}>Bolsas ({stats.bags})</button>
          <button style={{ ...s.btn, ...(folder === "variados" ? s.btnActive : {}) }} onClick={() => setFolder("variados")}>Variados ({stats.variados})</button>
          <input style={s.search} placeholder="Buscar por nome..." value={search} onChange={e => setSearch(e.target.value)} />
          <button style={s.btn} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <FontAwesomeIcon icon={faSpinner} spin /> : "↻ Atualizar"}
          </button>
        </div>
        <div style={{ color: "#888", fontSize: 12, marginBottom: 12 }}>
          {currentList.filter(i => !i.disabled).length} ativas · {currentList.filter(i => i.disabled).length} desativadas · Clique na foto para ativar/desativar imediatamente
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#d4a017" }}><FontAwesomeIcon icon={faSpinner} spin size="2x" /></div>
        ) : (
          <div style={s.grid}>
            {currentList.map(img => {
              const name = img.url.split("/").pop();
              const isToggling = toggling === img.url;
              return (
                <div
                  key={img.url}
                  style={{ ...s.imgCard, ...(img.disabled ? s.imgCardDisabled : s.imgCardActive) }}
                  onClick={() => !isToggling && handleToggle(img.url)}
                  title={name}
                >
                  <img src={img.url} alt={name} style={s.img} loading="lazy" />
                  <div style={{ ...s.badge, ...(img.disabled ? {} : s.badgeActive) }}>
                    {img.disabled ? "OFF" : "ON"}
                  </div>
                  {isToggling && (
                    <div style={{ ...s.overlay, opacity: 1 }}>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ color: "#d4a017", fontSize: 24 }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
